// DanceVideoSegmenter.tsx - Main component integrating all sub-components

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Play, Pause, FlipHorizontal, Save,
  Maximize, Minimize, Volume2, VolumeX, Share, Library, Edit2, Eye, EyeOff, Crop,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsLeft, SlidersHorizontal, Layers, ZoomIn, ZoomOut, Undo2,
  SquareScissors, ClipboardCopy
} from 'lucide-react';

// Import constants
import {
  APP_INFO,
  DEMO_CREDITS,
  UI_DIMENSIONS,
  BUTTON_COLORS,
  SOURCE_SEGMENT_COLOR,
  COMBO_SEGMENT_COLOR
} from '../constants';
import { getCurrentPlatformCapabilities } from '../platform/platformCapabilities';

// Import types and utilities
import {
  addBoundaryDraftSegmentAtTime,
  applyBoundaryDraftSegments,
  clampSegmentOverlapSeconds,
  createSegment,
  DEFAULT_SEGMENT_OVERLAP_SECONDS,
  getComboRange,
  getFirstComboId,
  getLargestSegmentDuration,
  getSourceSegments,
  handleSmoothTransition,
  isCombo,
  normalizeCombos,
  renameGeneratedSourceSegmentsByTimeline,
  removeSegmentsAndRepairCombos,
  resolveSegmentOverlapSeconds,
  resolveActiveComboId,
  saveCheckpoints
} from './utils';
import { 
  getContainerStyle, getVideoContainerStyle, getControlsStyle,
  successButtonStyle
} from './styles';
import type { Segment, SegmentState, NormalizedLibraryProject, SharedSession, TimelineLanePlacement, VideoViewCrop } from './types';
import { normalizeVideoViewCrop } from './types';
import { 
  calculateViewsRemaining,
  calculateDaysRemaining
} from '../utils/sharingHelpers';
import { describeVideoLoadFailure, VIDEO_INPUT_ACCEPT } from '../utils/videoFileTypes';

// Import components
import Timeline from './Timeline';
import BoundaryTimelineEditor from './BoundaryTimelineEditor';
import Sidebar from './Sidebar';
import SegmentsList from './SegmentsList';
import type { StudioTab } from './StudioList';
import LoadingIndicator from './LoadingIndicator';
import CroopBrandLogo from './CroopBrandLogo';
import LauncherVariantA from './launcher-prototype/LauncherVariantA';
import { formatCuratedChoreographyForClipboard } from './launcher-prototype/curatedChoreographies';
import type { CuratedPracticeDefaults } from './launcher-prototype/curatedChoreographies';
import { fetchYouTubeVideoMetadata } from './services/youtubeMetadataService';
import YouTubePracticePlayer from './YouTubePracticePlayer';
import VideoCropViewport from './VideoCropViewport';
import type { VideoCropFitMode } from './VideoCropViewport';
import VideoCropEditorOverlay from './VideoCropEditorOverlay';
import OrientationCheckOverlay from './OrientationCheckOverlay';
import { PracticeRuntimeAdapter, YOUTUBE_SUPPORTED_RATES } from './services/youtubePlayerAdapter';
import {
  DEFAULT_VIDEO_VIEW_CROP_SELECTION,
  FULL_FRAME_VIDEO_VIEW_CROP
} from './cropViewMath';

import AuthButton from './AuthButton';
import LibraryViewer  from './LibraryViewer';
import SegmentEditor from './SegmentEditor';
import PurchaseModal from './PurchaseModal';
import ShareModal from './ShareModal';
import { formatSpeedValue, getPresetStepperRate } from './SpeedSlider';
import SpeedTextInput, { stepLocalSpeed } from './SpeedTextInput';
import OverlapSlider from './OverlapSlider';
import ComboExtendGlyph from './ComboExtendGlyph';
import LoopGlyph from './LoopGlyph';
import './DanceVideoSegmenter.css';
import { migrateLegacyTransitionType } from './transitionCompat';

// Import hooks and services
import { useVideoState } from './hooks/useVideoState';
import { useSegmentState } from './hooks/useSegmentState';
import { FileUploadService } from './services/fileUploadService';
import { useUIControls } from './hooks/useUIControls';
import { useTimeline } from './hooks/useTimeline';
import { useAuth } from './hooks/useAuth';
import { useLibrary } from './hooks/useLibrary';
import { useSessionLoader } from './hooks/useSessionLoader';
import { 
  getActiveShareForVideo,
  createSharedSession,
  deactivateShare 
} from './services/sharedSessionService';
import {
  clearLibraryProjectShare,
  getLibraryProjectMembershipKey,
  isYouTubeLibraryProject,
  markLibraryProjectShared,
  persistLibraryProjectLoadMigrations
} from './services/videoLibrary';

const sortSegmentsByStartTime = (segments: Segment[]) => {
  return [...segments].sort((a, b) => a.startTime - b.startTime);
};

const cloneSegmentsForUndo = (segments: Segment[]): Segment[] => (
  segments.map(segment => ({
    ...segment,
    ...(segment.sourceSegmentIds ? { sourceSegmentIds: [...segment.sourceSegmentIds] } : {})
  }))
);

// Every speed the YouTube player accepts (0.25x to 2x); typed speeds snap to the nearest one.
const YOUTUBE_SPEED_PRESETS = Array.from(YOUTUBE_SUPPORTED_RATES).sort((a, b) => a - b);

type YouTubePracticeRuntime = {
  videoId: string;
  startTime?: number;
  runtimeKey: string;
  autoSaveOnFirstLoad: boolean;
};

const getSupportedYouTubePlaybackRate = (rate: number | undefined): number => {
  return rate !== undefined && YOUTUBE_SUPPORTED_RATES.has(rate) ? rate : 1;
};

const isMergedSegment = (segment: Segment) => isCombo(segment);

const isStudioTabSegment = (segment: Segment, tab: StudioTab) => {
  return tab === 'combos' ? isMergedSegment(segment) : !isMergedSegment(segment);
};

const getBoundaryEditableSegments = (segments: Segment[]) => {
  return sortSegmentsByStartTime(segments.filter(segment => !isMergedSegment(segment)));
};

// On-video controls stay visible this long after the last interaction while playing.
const PRACTICE_OVERLAY_HIDE_MS = 2500;
const SPEED_FLASH_MS = 900;
const DOUBLE_TAP_FULLSCREEN_MS = 320;
// How long to wait for YouTube to start playing with sound before retrying muted.
const YOUTUBE_AUTOPLAY_FALLBACK_MS = 1500;
// Within this much time of a loop end, the jump back is scheduled for the exact moment.
const YOUTUBE_LOOP_SCHEDULE_WINDOW_MS = 260;
const DISABLED_SEGMENT_SKIP_EPSILON = 0.02;
const TIMELINE_ZOOM_LEVELS = [1, 1.5, 2, 3, 4];
const getNearestTimelineZoomIndex = (zoom: number) => TIMELINE_ZOOM_LEVELS.reduce(
  (best, level, index) => (
    Math.abs(level - zoom) < Math.abs(TIMELINE_ZOOM_LEVELS[best] - zoom) ? index : best
  ),
  0
);
const SMOOTH_SCRUB_PREVIEW_INTERVAL_MS = 75;

const getDisabledSegmentSkipTarget = (
  currentTime: number,
  duration: number,
  segments: Segment[]
): number | null => {
  if (segments.length === 0) return null;

  const disabledSegments = sortSegmentsByStartTime(
    segments.filter(segment => Boolean(segment.isDisabled))
  );

  if (disabledSegments.length === 0) return null;

  let nextTime = currentTime;
  let skipped = false;
  const maxSkips = disabledSegments.length + 2;

  for (let skipCount = 0; skipCount < maxSkips; skipCount += 1) {
    let blockingSegment: Segment | null = null;
    for (const segment of disabledSegments) {
      if (nextTime >= segment.startTime && nextTime < segment.endTime - 0.0001) {
        blockingSegment = segment;
        break;
      }
    }

    if (!blockingSegment) break;

    skipped = true;
    nextTime = Math.min(duration, blockingSegment.endTime + DISABLED_SEGMENT_SKIP_EPSILON);

    if (nextTime >= duration) break;
  }

  return skipped ? nextTime : null;
};

const readViewportHeight = (): number => {
  if (typeof window === 'undefined') return 0;
  return window.visualViewport?.height ?? window.innerHeight ?? 0;
};

const DanceVideoSegmenter: React.FC = () => {
  const location = useLocation();

  // Video state
  const {
    videoState,
    setVideoState,
    videoRef,
    togglePlay,
    toggleMirror,
    toggleMute,
    seekToTime,
    handleVideoLoaded,
    handleVideoError
  } = useVideoState();

  // Segments state
  const {
    segmentState,
    setSegmentState,
    toggleLoop,
    deleteSegment,
    saveSegmentName,
    toggleSegmentLearned,
    createComboFromActiveSegment,
    getComboExtensionIds,
    extendComboFromActive,
    duplicateCombo,
    deleteCombo,
    setActiveSegment,
    activateSegmentForPlay,
    commitSegmentState,
    canUndoSegmentChange,
    undoLastSegmentChange,
    clearSegmentUndoHistory,
    resizeComboEdge
  } = useSegmentState();

  // Store the original file for sharing
  const [originalVideoFile, setOriginalVideoFile] = useState<File | null>(null);

  // YouTube practice state. Direct first loads can create a signed-in library
  // project, but playback itself still runs through the YouTube iframe.
  const [youtubePractice, setYoutubePractice] = useState<YouTubePracticeRuntime | null>(null);
  const youtubeAdapterRef = useRef<PracticeRuntimeAdapter | null>(null);
  const youtubeRuntimeCounterRef = useRef(0);
  // Set once the current YouTube video has actually started playing (see the autoplay fallback).
  const youtubeHasPlayedRef = useRef(false);
  // Precise YouTube looping: a timer set just before the loop end, plus the latest loop target.
  const youtubeLoopTimerRef = useRef<number | null>(null);
  const youtubeLoopStateRef = useRef<{ isLooping: boolean; activeSegmentId: string | null; loopStart: number }>({
    isLooping: false,
    activeSegmentId: null,
    loopStart: 0
  });
  // Curated pick's default combo: seek to it once the YouTube player is ready.
  const pendingDefaultComboSeekRef = useRef<string | null>(null);
  const isSeekingRef = useRef(false);
  const isTimelineScrubbingRef = useRef(false);
  const smoothScrubWasPlayingRef = useRef(false);
  const smoothScrubPreviewFrameRef = useRef<number | null>(null);
  const smoothScrubPreviewTimeRef = useRef<number | null>(null);
  const smoothScrubLastSeekAtRef = useRef(0);

  // UI state
  const {
    editingSegment,
    isFullscreen,
    practiceMode,
    showMergedTimeline,
    isDemo,
    containerRef,
    segmentNameInputRef,
    setIsDemo,
    toggleFullscreen,
    togglePracticeMode,
    toggleMergedTimeline,
    startEditingSegment,
    stopEditingSegment
  } = useUIControls();
  const [studioTab, setStudioTab] = useState<StudioTab>('segments');
  const [showAllCombos, setShowAllCombos] = useState(false);

  // Timeline hook
  const { timelineRef,
     handleTimelineClick: handleTimelineClickHelper 
    } = useTimeline();

  // Auth hook
  const {
    userProfile,
    handleSignInGoogle,
    handleSignInEmail,
    handleSignUpEmail,
    handleResetPassword,
    handleSignOut,
    handleDeleteAccount,
    refreshProfile,
    isAuthenticated
  } = useAuth();

  // Handle profile refresh after payment
  useEffect(() => {
    const state = location.state as { refreshProfile?: boolean } | null;
    if (state?.refreshProfile) {
      console.log('🔄 Triggering profile refresh after payment');
      refreshProfile();
      // Clear the state to prevent repeated refreshes
      window.history.replaceState({}, document.title);
    }
  }, [location, refreshProfile]);

  // Library hook
  const library = useLibrary({
    isAuthenticated,
    userProfile,
    refreshProfile
  });
  const platformCapabilities = getCurrentPlatformCapabilities();
  const sharingEnabled = platformCapabilities.canExposeSharing;
  const paymentUpgradeEnabled = platformCapabilities.canShowPaymentUpgradeUi;
  const devToolsEnabled = platformCapabilities.canAccessDevOnlyRoutes;

  const [showLibrary, setShowLibrary] = useState(false);
  const [isPurchaseOpen, setPurchaseOpen] = useState(false);
  const [brandAnimationKey, setBrandAnimationKey] = useState(0);

  // Segment editor state
  const [showSegmentEditor, setShowSegmentEditor] = useState(false);
  const [isBoundaryEditorOpen, setIsBoundaryEditorOpen] = useState(false);
  const [isSegmentEditMode, setIsSegmentEditMode] = useState(false);
  const [isOrientationCheckOpen, setIsOrientationCheckOpen] = useState(false);
  const [boundaryDraftSegments, setBoundaryDraftSegments] = useState<Segment[]>([]);
  const [boundaryDraftUndoStack, setBoundaryDraftUndoStack] = useState<Segment[][]>([]);
  const [, setHasBoundaryDraftChanges] = useState(false);
  const checkpointsFileInputRef = useRef<HTMLInputElement | null>(null);
  const videoFileInputRef = useRef<HTMLInputElement | null>(null);
  // Auto-save: guest projects use IndexedDB; signed-in projects use Firebase.
  const autoSaveAttemptedFileRef = useRef<File | null>(null);
  const autoSaveAttemptedYouTubeRuntimeRef = useRef<string | null>(null);
  const autoSaveGenerationRef = useRef(0);
  const segmentAutoSaveTimerRef = useRef<number | null>(null);

  const invalidatePendingAutoSave = useCallback(() => {
    autoSaveGenerationRef.current += 1;
    autoSaveAttemptedFileRef.current = null;
    autoSaveAttemptedYouTubeRuntimeRef.current = null;
    if (segmentAutoSaveTimerRef.current !== null) {
      window.clearTimeout(segmentAutoSaveTimerRef.current);
      segmentAutoSaveTimerRef.current = null;
    }
  }, []);

  useSessionLoader({
  setVideoState,
  setSegmentState,
  setIsDemo,
  setOriginalVideoFile
  });

  // Auto-save (1/2): when a freshly uploaded video is ready, save it
  // to the library automatically — even with no segments yet. Demos (no file),
  // YouTube practice, and videos loaded from the library are excluded. Deduped
  // per File so it fires once; failures leave the video usable.
  useEffect(() => {
    if (!originalVideoFile || isDemo || youtubePractice) return;
    if (!videoState.videoSrc) return;
    if (library.isSavedToLibrary || library.loadedFromLibraryId) return;
    if (autoSaveAttemptedFileRef.current === originalVideoFile) return;
    autoSaveAttemptedFileRef.current = originalVideoFile;
    void library.handleSaveToLibrary(originalVideoFile, videoState, segmentState, {
      auto: true,
      autoSaveKey: `upload-${autoSaveGenerationRef.current}`
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, originalVideoFile, isDemo, youtubePractice, videoState.videoSrc, library.isSavedToLibrary, library.loadedFromLibraryId]);

  // Auto-save direct YouTube first loads. This creates the library
  // project immediately, then the debounced project update below keeps settings
  // and future segments in sync.
  useEffect(() => {
    if (!youtubePractice?.autoSaveOnFirstLoad || isDemo) return;
    if (library.isSavedToLibrary || library.loadedFromLibraryId) return;
    if (autoSaveAttemptedYouTubeRuntimeRef.current === youtubePractice.runtimeKey) return;

    autoSaveAttemptedYouTubeRuntimeRef.current = youtubePractice.runtimeKey;
    void library.handleSaveYouTubeProject(youtubePractice, videoState, segmentState, {
      auto: true,
      autoSaveKey: `youtube-${autoSaveGenerationRef.current}-${youtubePractice.runtimeKey}`
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, youtubePractice, isDemo, library.isSavedToLibrary, library.loadedFromLibraryId]);

  const playbackSegments = isBoundaryEditorOpen ? boundaryDraftSegments : segmentState.segments;
  const maxSegmentOverlapSeconds = getLargestSegmentDuration(playbackSegments);

  // Auto-save (2/2): once the project exists, persist segment/settings
  // changes in the background (debounced). Boundary edits save the current draft
  // so first-created segments and mirror/overlap changes do not wait for exit.
  useEffect(() => {
    if (!library.canUpdateSegments) return;
    if (segmentAutoSaveTimerRef.current !== null) {
      window.clearTimeout(segmentAutoSaveTimerRef.current);
    }
    segmentAutoSaveTimerRef.current = window.setTimeout(() => {
      const stateForAutoSave = isBoundaryEditorOpen
        ? {
            ...segmentState,
            segments: applyBoundaryDraftSegments(segmentState.segments, boundaryDraftSegments)
          }
        : segmentState;
      void library.handleUpdateSegments(videoState, stateForAutoSave, { auto: true });
    }, 1200);
    return () => {
      if (segmentAutoSaveTimerRef.current !== null) {
        window.clearTimeout(segmentAutoSaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    segmentState.segments,
    segmentState.segmentOverlap,
    segmentState.isLooping,
    segmentState.activeSegment,
    segmentState.transitionType,
    videoState.isMirrored,
    videoState.playbackRate,
    videoState.userMutePreference,
    videoState.viewCrop,
    videoState.timelineZoom,
    boundaryDraftSegments,
    isAuthenticated,
    library.canUpdateSegments,
    isBoundaryEditorOpen
  ]);

  // Sharing modal state
  const [currentShareData, setCurrentShareData] = useState<SharedSession | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const comboSegmentsCount = segmentState.segments.filter(isMergedSegment).length;
  const hasComboSegments = comboSegmentsCount > 0;
  const [isMobileControlsViewport, setIsMobileControlsViewport] = useState(false);
  const [isAdvancedControlsOpen, setIsAdvancedControlsOpen] = useState(false);
  // Zoom lives in videoState so it is saved with the project's settings.
  const timelineZoomIndex = getNearestTimelineZoomIndex(videoState.timelineZoom ?? 1);
  const setTimelineZoomIndex = useCallback((next: number | ((index: number) => number)) => {
    setVideoState(prev => {
      const currentIndex = getNearestTimelineZoomIndex(prev.timelineZoom ?? 1);
      const nextIndex = typeof next === 'function' ? next(currentIndex) : next;
      return { ...prev, timelineZoom: TIMELINE_ZOOM_LEVELS[nextIndex] ?? 1 };
    });
  }, [setVideoState]);
  const [controlsContainerWidth, setControlsContainerWidth] = useState(0);
  const [videoStageHeight, setVideoStageHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(readViewportHeight);
  const [isFullscreenControlsVisible, setIsFullscreenControlsVisible] = useState(true);
  const [isFullscreenSegmentsPanelOpen, setIsFullscreenSegmentsPanelOpen] = useState(false);
  const [isCropEditorOpen, setIsCropEditorOpen] = useState(false);
  const [uploadedVideoAspectRatio, setUploadedVideoAspectRatio] = useState<number | null>(null);
  const [draftViewCrop, setDraftViewCrop] = useState<VideoViewCrop>(DEFAULT_VIDEO_VIEW_CROP_SELECTION);
  const hasPracticeMedia = Boolean(videoState.videoSrc) || Boolean(youtubePractice);
  const shouldShowOrientationCheck = isOrientationCheckOpen && hasPracticeMedia && !videoState.isLoading;
  const fullscreenControlsTimeoutRef = useRef<number | null>(null);
  const [isPracticeOverlayActive, setIsPracticeOverlayActive] = useState(true);
  const practiceOverlayTimeoutRef = useRef<number | null>(null);
  const [speedFlash, setSpeedFlash] = useState<{ text: string; key: number } | null>(null);
  const speedFlashTimeoutRef = useRef<number | null>(null);
  const videoStageRef = useRef<HTMLDivElement | null>(null);
  const cropEditorInitialViewCropRef = useRef<VideoViewCrop | undefined>(undefined);

  useEffect(() => {
    if (maxSegmentOverlapSeconds <= 0) return;

    setSegmentState(prev => {
      const nextOverlapSeconds = clampSegmentOverlapSeconds(prev.segmentOverlap, maxSegmentOverlapSeconds);
      if (Math.abs(nextOverlapSeconds - prev.segmentOverlap) < 0.001) return prev;

      return {
        ...prev,
        segmentOverlap: nextOverlapSeconds
      };
    });
  }, [maxSegmentOverlapSeconds, setSegmentState]);

  const resetBoundaryEditorState = useCallback(() => {
    setIsBoundaryEditorOpen(false);
    setBoundaryDraftSegments([]);
    setBoundaryDraftUndoStack([]);
    setHasBoundaryDraftChanges(false);
  }, []);

  const recordBoundaryDraftSnapshot = useCallback(() => {
    setBoundaryDraftUndoStack(previousSnapshots => [
      ...previousSnapshots.slice(-39),
      cloneSegmentsForUndo(boundaryDraftSegments)
    ]);
  }, [boundaryDraftSegments]);

  const handleStudioTabChange = useCallback((tab: StudioTab) => {
    setStudioTab(tab === 'combos' && comboSegmentsCount === 0 ? 'segments' : tab);
  }, [comboSegmentsCount]);

  useEffect(() => {
    if (comboSegmentsCount === 0) {
      setStudioTab('segments');
      setShowAllCombos(false);
    }
  }, [comboSegmentsCount]);

  useEffect(() => {
    if (isDemo) {
      resetBoundaryEditorState();
    }
  }, [isDemo, resetBoundaryEditorState]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(pointer: coarse) and (max-width: 900px)');
    const syncControlsViewport = () => {
      setIsMobileControlsViewport(mediaQuery.matches);
    };

    syncControlsViewport();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncControlsViewport);
      return () => {
        mediaQuery.removeEventListener('change', syncControlsViewport);
      };
    }

    mediaQuery.addListener(syncControlsViewport);
    return () => {
      mediaQuery.removeListener(syncControlsViewport);
    };
  }, []);

  // Intrinsic video dimensions arrive asynchronously, so they are tracked as state rather than read
  // off the ref during render; anything that branches on aspect ratio needs a re-render when they land.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      setUploadedVideoAspectRatio(null);
      return;
    }

    const syncAspectRatio = () => {
      const { videoWidth, videoHeight } = video;
      setUploadedVideoAspectRatio(
        videoWidth && videoHeight ? videoWidth / videoHeight : null
      );
    };

    // Metadata can land before this effect runs, so read once instead of only waiting on the event.
    syncAspectRatio();
    // `emptied` fires when a new source restarts the load algorithm and clears the old dimensions.
    video.addEventListener('loadedmetadata', syncAspectRatio);
    video.addEventListener('emptied', syncAspectRatio);

    return () => {
      video.removeEventListener('loadedmetadata', syncAspectRatio);
      video.removeEventListener('emptied', syncAspectRatio);
    };
  }, [videoRef, videoState.videoSrc]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setControlsContainerWidth(entry?.contentRect.width ?? 0);
    });

    observer.observe(root);
    return () => observer.disconnect();
  }, [containerRef, isFullscreen]);

  useEffect(() => {
    const stage = videoStageRef.current;
    if (!stage) {
      setVideoStageHeight(0);
      return;
    }

    const readStageHeight = () => (
      stage.getBoundingClientRect().height || stage.offsetHeight || 0
    );
    const syncStageHeight = () => {
      setVideoStageHeight(readStageHeight());
    };

    syncStageHeight();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entryHeight = entries[0]?.contentRect.height;
      setVideoStageHeight(entryHeight || readStageHeight());
    });

    observer.observe(stage);
    return () => observer.disconnect();
  }, [isFullscreen, videoState.videoSrc, videoState.isLoading, youtubePractice?.runtimeKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncViewportHeight = () => {
      setViewportHeight(readViewportHeight());
    };
    const visualViewport = window.visualViewport;

    syncViewportHeight();
    window.addEventListener('resize', syncViewportHeight);
    visualViewport?.addEventListener('resize', syncViewportHeight);

    return () => {
      window.removeEventListener('resize', syncViewportHeight);
      visualViewport?.removeEventListener('resize', syncViewportHeight);
    };
  }, []);

  const revealFullscreenControls = useCallback(() => {
    if (!isFullscreen) return;

    setIsFullscreenControlsVisible(true);

    if (fullscreenControlsTimeoutRef.current !== null) {
      window.clearTimeout(fullscreenControlsTimeoutRef.current);
      fullscreenControlsTimeoutRef.current = null;
    }

    if (videoState.isPlaying && !isBoundaryEditorOpen) {
      fullscreenControlsTimeoutRef.current = window.setTimeout(() => {
        setIsFullscreenControlsVisible(false);
      }, 2200);
    }
  }, [isBoundaryEditorOpen, isFullscreen, videoState.isPlaying]);

  useEffect(() => {
    if (!isFullscreen) {
      setIsFullscreenControlsVisible(true);
      if (fullscreenControlsTimeoutRef.current !== null) {
        window.clearTimeout(fullscreenControlsTimeoutRef.current);
        fullscreenControlsTimeoutRef.current = null;
      }
      return;
    }

    revealFullscreenControls();

    const handleActivity = () => {
      revealFullscreenControls();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      if (fullscreenControlsTimeoutRef.current !== null) {
        window.clearTimeout(fullscreenControlsTimeoutRef.current);
        fullscreenControlsTimeoutRef.current = null;
      }
    };
  }, [isFullscreen, revealFullscreenControls]);

  useEffect(() => {
    setIsAdvancedControlsOpen(false);
  }, [isFullscreen]);


  useEffect(() => {
    if (!hasPracticeMedia || !isAdvancedControlsOpen || videoState.isLoading) {
      setIsCropEditorOpen(false);
      setDraftViewCrop(DEFAULT_VIDEO_VIEW_CROP_SELECTION);
    }
  }, [hasPracticeMedia, isAdvancedControlsOpen, videoState.isLoading]);

  const isMobileFullscreenSidebarMode = isFullscreen && isMobileControlsViewport && !practiceMode;
  // The right-edge tab: on phones it slides the studio panel over the video; on desktop it
  // shows/hides the fullscreen sidebar (practice mode = sidebar hidden).
  const isStudioPanelShown = isMobileControlsViewport
    ? isMobileFullscreenSidebarMode && isFullscreenSegmentsPanelOpen
    : !practiceMode;
  const toggleStudioPanel = () => {
    if (!isMobileControlsViewport) {
      togglePracticeMode();
      return;
    }
    if (practiceMode) {
      togglePracticeMode();
    }
    setIsFullscreenSegmentsPanelOpen(previous => !previous);
  };

  useEffect(() => {
    if (!isMobileFullscreenSidebarMode) {
      setIsFullscreenSegmentsPanelOpen(false);
    }
  }, [isMobileFullscreenSidebarMode]);

  useEffect(() => {
    return () => {
      if (fullscreenControlsTimeoutRef.current !== null) {
        window.clearTimeout(fullscreenControlsTimeoutRef.current);
      }
      if (practiceOverlayTimeoutRef.current !== null) {
        window.clearTimeout(practiceOverlayTimeoutRef.current);
      }
      if (speedFlashTimeoutRef.current !== null) {
        window.clearTimeout(speedFlashTimeoutRef.current);
      }
    };
  }, []);
  

  // Clean up object URL when component unmounts or video source changes
  useEffect(() => {
    const currentVideoSrc = videoState.videoSrc;
    return () => {
      FileUploadService.cleanupVideoUrl(currentVideoSrc);
    };
  }, [videoState.videoSrc]);


  // Smart muting: automatically mute when looping starts, but respect user preference
  useEffect(() => {
    if (videoRef.current) {
      if (segmentState.isLooping) {
        const shouldMute = videoState.userMutePreference === false ? true : videoState.userMutePreference;
        setVideoState(prev => ({ ...prev, isMuted: shouldMute }));
        videoRef.current.muted = shouldMute;
      } else {
        const shouldMute = videoState.userMutePreference;
        setVideoState(prev => ({ ...prev, isMuted: shouldMute }));
        videoRef.current.muted = shouldMute;
      }
    }
  }, [segmentState.isLooping, videoState.userMutePreference, setVideoState, videoRef]);

  // ---- YouTube practice handlers ----

  const createYouTubePracticeRuntime = (
    videoId: string,
    startTime: number | undefined,
    sourceKey: string
  ): YouTubePracticeRuntime => {
    youtubeRuntimeCounterRef.current += 1;
    const startKey = startTime ?? 'initial';
    return {
      videoId,
      startTime,
      runtimeKey: `${sourceKey}:${videoId}:${startKey}:${youtubeRuntimeCounterRef.current}`,
      autoSaveOnFirstLoad: sourceKey === 'direct'
    };
  };

  const handlePracticeYouTube = (
    videoId: string,
    startTime?: number,
    presetSegments?: Segment[],
    presetDefaults: CuratedPracticeDefaults = {}
  ) => {
    // Clear any loaded upload video first.
    FileUploadService.cleanupVideoUrl(videoState.videoSrc);
    invalidatePendingAutoSave();
    library.resetLibraryState();
    setOriginalVideoFile(null);
    setVideoState(prev => ({
      ...prev,
      videoSrc: null,
      videoFileName: `https://www.youtube.com/watch?v=${videoId}`,
      isLoading: false,
      loadingSource: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackRate: getSupportedYouTubePlaybackRate(presetDefaults.playbackRate ?? prev.playbackRate),
      isMirrored: presetDefaults.isMirrored ?? prev.isMirrored,
      isMuted: presetDefaults.isMuted ?? prev.isMuted,
      userMutePreference: presetDefaults.isMuted ?? prev.userMutePreference,
      viewCrop: normalizeVideoViewCrop(presetDefaults.viewCrop),
      timelineZoom: presetDefaults.timelineZoom !== undefined
        ? TIMELINE_ZOOM_LEVELS[getNearestTimelineZoomIndex(presetDefaults.timelineZoom)]
        : prev.timelineZoom,
    }));
    // A curated pick can name a combo to select and loop straight away.
    const defaultComboId = presetDefaults.activeComboId &&
      presetSegments?.some(segment => segment.id === presetDefaults.activeComboId && isCombo(segment))
      ? presetDefaults.activeComboId
      : null;
    pendingDefaultComboSeekRef.current = defaultComboId;
    setSegmentState(prev => ({
      ...prev,
      segments: presetSegments ?? [],
      activeSegment: defaultComboId,
      activeComboId: defaultComboId,
      isMarkingSegment: false,
      currentSegmentStart: null,
      isLooping: Boolean(defaultComboId),
    }));
    if (defaultComboId && !showMergedTimeline) {
      toggleMergedTimeline();
    }
    clearSegmentUndoHistory();
    youtubeAdapterRef.current = null;
    setStudioTab(presetSegments?.some(isCombo) ? 'combos' : 'segments');
    // Curated picks arrive pre-segmented — skip the "new video" onboarding
    // (mirror check + forced segment-edit mode) and land straight in
    // practice mode. A blank/pasted video still gets both, since neither
    // its segments nor its orientation are known yet.
    const isPresegmented = Boolean(presetSegments && presetSegments.length > 0);
    setIsSegmentEditMode(!isPresegmented);
    setIsOrientationCheckOpen(!isPresegmented);
    // A curated pick must never auto-save itself to the library — the user
    // hasn't confirmed anything yet, and every fresh visit to the same card
    // would otherwise create another duplicate entry (saveYouTubeProject has
    // no existing-project check; it always creates a new one). Only a raw
    // pasted/typed URL — where segments are the user's own unsaved work —
    // gets the "don't lose my progress" auto-save.
    setYoutubePractice(createYouTubePracticeRuntime(videoId, startTime, isPresegmented ? 'curated' : 'direct'));
  };

  // Dev-only authoring tool: copy the segments/combos just marked (by
  // actually watching the video) as paste-ready CuratedChoreography source,
  // so curated presets are authored for real rather than guessed.
  const handleExportCuratedPreset = async (segmentsToExport: Segment[]) => {
    if (!youtubePractice) return;

    let defaultTitle = '';
    try {
      const metadata = await fetchYouTubeVideoMetadata(youtubePractice.videoId);
      defaultTitle = metadata?.title ?? '';
    } catch {
      // Best-effort only — fall back to an empty title prompt.
    }

    const title = window.prompt('Title for this curated pick:', defaultTitle);
    if (!title) return;

    const snippet = formatCuratedChoreographyForClipboard(
      youtubePractice.videoId,
      title,
      youtubePractice.startTime,
      segmentsToExport,
      {
        isMirrored: videoState.isMirrored,
        viewCrop: normalizeVideoViewCrop(videoState.viewCrop),
        playbackRate: videoState.playbackRate,
        isMuted: videoState.userMutePreference,
        timelineZoom: TIMELINE_ZOOM_LEVELS[timelineZoomIndex] ?? 1,
        // Only a combo that is actually looping becomes the default.
        activeComboId: segmentState.isLooping && segmentState.activeSegment &&
          segmentsToExport.some(segment => segment.id === segmentState.activeSegment && isCombo(segment))
          ? segmentState.activeSegment
          : undefined,
      }
    );

    try {
      await navigator.clipboard.writeText(snippet);
      alert('Copied! Paste this entry into ALL_TIME_CHOREOGRAPHIES or TRENDING_CHOREOGRAPHIES in curatedChoreographies.ts.');
    } catch {
      window.prompt('Copy this preset (Ctrl/Cmd+C, then Enter):', snippet);
    }
  };

  // Called by YouTubePracticePlayer at ~200 ms intervals; drives loop detection.
  const handleYouTubeTimeUpdate = (currentTime: number, duration: number) => {
    setVideoState(prev => ({ ...prev, currentTime, duration }));

    if (isTimelineScrubbingRef.current) return;

    const activeSegment = segmentState.activeSegment
      ? playbackSegments.find(s => s.id === segmentState.activeSegment) ?? null
      : null;

    if (segmentState.isLooping && activeSegment?.isDisabled) {
      setSegmentState(prev => ({
        ...prev,
        isLooping: false,
        activeSegment: prev.activeSegment === activeSegment.id ? null : prev.activeSegment,
      }));
      return;
    }

    if (!segmentState.isLooping || !activeSegment || isSeekingRef.current) return;

    const overlapSeconds = Math.max(0, segmentState.segmentOverlap);
    const loopStart = Math.max(0, activeSegment.startTime - overlapSeconds);

    if (currentTime >= activeSegment.endTime) {
      clearYouTubeLoopTimer();
      performYouTubeLoopSeek(loopStart);
      return;
    }

    // The poll only runs every ~100 ms (later on a busy phone), so waiting for it to notice the end
    // makes loops overshoot. When the end is close, time the jump back for the exact moment instead.
    if (youtubeLoopTimerRef.current !== null) return;
    const rate = videoState.playbackRate > 0 ? videoState.playbackRate : 1;
    const remainingMs = ((activeSegment.endTime - currentTime) / rate) * 1000;
    if (remainingMs > YOUTUBE_LOOP_SCHEDULE_WINDOW_MS) return;

    const segmentId = activeSegment.id;
    const endTime = activeSegment.endTime;
    youtubeLoopTimerRef.current = window.setTimeout(() => {
      youtubeLoopTimerRef.current = null;
      const adapter = youtubeAdapterRef.current;
      const latest = youtubeLoopStateRef.current;
      if (!adapter || isSeekingRef.current || !latest.isLooping || latest.activeSegmentId !== segmentId) return;
      // Skip if the playhead has moved away from the end in the meantime (scrubbed, jumped).
      const time = adapter.getCurrentTime();
      if (time < endTime - 0.35 || time > endTime + 1) return;
      performYouTubeLoopSeek(latest.loopStart);
    }, Math.max(0, remainingMs));
  };

  const performYouTubeLoopSeek = (loopStart: number) => {
    isSeekingRef.current = true;
    youtubeAdapterRef.current?.seek(loopStart);
    // Clear the seek-gate after YT has had time to start buffering. The PLAYING event
    // also clears it (see handleYouTubePlayStateChange), whichever fires first.
    setTimeout(() => { isSeekingRef.current = false; }, 250);
  };

  const clearYouTubeLoopTimer = () => {
    if (youtubeLoopTimerRef.current !== null) {
      window.clearTimeout(youtubeLoopTimerRef.current);
      youtubeLoopTimerRef.current = null;
    }
  };

  const handleYouTubePlayStateChange = (isPlaying: boolean) => {
    if (isPlaying) youtubeHasPlayedRef.current = true;
    if (isSeekingRef.current) {
      if (isPlaying) {
        isSeekingRef.current = false;
      }
      return;
    }
    setVideoState(prev => ({ ...prev, isPlaying }));
  };

  const handleSelectOrientation = useCallback((isMirrored: boolean) => {
    setVideoState(prev => ({ ...prev, isMirrored }));
  }, [setVideoState]);

  const handleConfirmOrientation = useCallback(() => {
    setIsOrientationCheckOpen(false);
  }, []);

  // YouTube-aware play/pause: routes through adapter when in YouTube mode.
  const handleTogglePlay = () => {
    if (shouldShowOrientationCheck) {
      return;
    }

    if (youtubeAdapterRef.current) {
      if (videoState.isPlaying) {
        youtubeAdapterRef.current.pause();
      } else {
        youtubeAdapterRef.current.play();
      }
      setVideoState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
    } else {
      togglePlay();
    }
  };

  // YouTube-aware mute: routes through adapter when in YouTube mode.
  const handleToggleMute = () => {
    if (youtubeAdapterRef.current) {
      const newMuted = !videoState.isMuted;
      youtubeAdapterRef.current.mute(newMuted);
      setVideoState(prev => ({ ...prev, isMuted: newMuted, userMutePreference: newMuted }));
    } else {
      toggleMute();
    }
  };

  // YouTube-aware seek.
  const seekToTimeActive = (time: number) => {
    if (youtubeAdapterRef.current) {
      youtubeAdapterRef.current.seek(time);
      setVideoState(prev => ({ ...prev, currentTime: time }));
    } else {
      seekToTime(time);
    }
  };

  // "Back 1s" in edit mode: step the playhead by a few seconds, within the video.
  const seekBySecondsActive = (deltaSeconds: number) => {
    const adapter = youtubeAdapterRef.current;
    const current = adapter
      ? adapter.getCurrentTime()
      : videoRef.current?.currentTime ?? videoState.currentTime;
    const duration = adapter
      ? adapter.getDuration()
      : videoRef.current?.duration ?? videoState.duration;
    const upperBound = duration > 0 ? duration : Number.POSITIVE_INFINITY;
    seekToTimeActive(Math.min(upperBound, Math.max(0, current + deltaSeconds)));
  };

  const clampSmoothScrubTime = useCallback((time: number): number => {
    const adapterDuration = youtubeAdapterRef.current?.getDuration() ?? 0;
    const nativeDuration = videoRef.current?.duration ?? 0;
    const knownDuration = adapterDuration > 0
      ? adapterDuration
      : videoState.duration > 0
      ? videoState.duration
      : nativeDuration;

    const safeTime = Number.isFinite(time) ? Math.max(0, time) : 0;
    return knownDuration > 0 ? Math.min(safeTime, knownDuration) : safeTime;
  }, [videoRef, videoState.duration]);

  const cancelSmoothScrubPreviewFrame = useCallback(() => {
    const frameId = smoothScrubPreviewFrameRef.current;
    if (frameId === null) return;

    if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(frameId);
    } else {
      window.clearTimeout(frameId);
    }
    smoothScrubPreviewFrameRef.current = null;
  }, []);

  const seekPracticeMediaForScrub = useCallback((time: number, precise: boolean): number => {
    const clampedTime = clampSmoothScrubTime(time);
    const adapter = youtubeAdapterRef.current;

    if (adapter) {
      adapter.seek(clampedTime);
    } else if (videoRef.current) {
      const video = videoRef.current;
      if (!precise && typeof video.fastSeek === 'function') {
        video.fastSeek(clampedTime);
      } else {
        video.currentTime = clampedTime;
      }
    }

    setVideoState(prev => ({ ...prev, currentTime: clampedTime }));
    return clampedTime;
  }, [clampSmoothScrubTime, setVideoState, videoRef]);

  const applySmoothScrubPreview = useCallback((time: number, force = false) => {
    const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
    if (!force && now - smoothScrubLastSeekAtRef.current < SMOOTH_SCRUB_PREVIEW_INTERVAL_MS) {
      return;
    }

    smoothScrubLastSeekAtRef.current = now;
    seekPracticeMediaForScrub(time, false);
  }, [seekPracticeMediaForScrub]);

  const queueSmoothScrubPreview = useCallback((time: number) => {
    smoothScrubPreviewTimeRef.current = time;
    if (smoothScrubPreviewFrameRef.current !== null) return;

    const runPreview = () => {
      smoothScrubPreviewFrameRef.current = null;
      const previewTime = smoothScrubPreviewTimeRef.current;
      smoothScrubPreviewTimeRef.current = null;
      if (previewTime !== null) {
        applySmoothScrubPreview(previewTime);
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      smoothScrubPreviewFrameRef.current = window.requestAnimationFrame(runPreview);
    } else {
      smoothScrubPreviewFrameRef.current = window.setTimeout(runPreview, 16);
    }
  }, [applySmoothScrubPreview]);

  const handleSmoothScrubStart = useCallback((time: number) => {
    const adapter = youtubeAdapterRef.current;
    const video = videoRef.current;
    const wasPlaying = adapter ? videoState.isPlaying : Boolean(video && !video.paused);

    isTimelineScrubbingRef.current = true;
    smoothScrubWasPlayingRef.current = wasPlaying;
    smoothScrubPreviewTimeRef.current = null;
    smoothScrubLastSeekAtRef.current = 0;
    cancelSmoothScrubPreviewFrame();

    if (wasPlaying) {
      if (adapter) {
        adapter.pause();
      } else {
        video?.pause();
      }
    }

    setVideoState(prev => ({
      ...prev,
      currentTime: clampSmoothScrubTime(time),
      isPlaying: false
    }));
    applySmoothScrubPreview(time, true);
  }, [
    applySmoothScrubPreview,
    cancelSmoothScrubPreviewFrame,
    clampSmoothScrubTime,
    setVideoState,
    videoRef,
    videoState.isPlaying
  ]);

  const handleSmoothScrubPreview = useCallback((time: number) => {
    if (!isTimelineScrubbingRef.current) return;
    queueSmoothScrubPreview(time);
  }, [queueSmoothScrubPreview]);

  const handleSmoothScrubEnd = useCallback((time: number) => {
    const shouldResumePlayback = smoothScrubWasPlayingRef.current;
    cancelSmoothScrubPreviewFrame();
    smoothScrubPreviewTimeRef.current = null;
    smoothScrubLastSeekAtRef.current = 0;

    const finalTime = seekPracticeMediaForScrub(time, true);
    isTimelineScrubbingRef.current = false;
    smoothScrubWasPlayingRef.current = false;

    if (shouldResumePlayback) {
      const adapter = youtubeAdapterRef.current;
      if (adapter) {
        adapter.play();
      } else {
        const playPromise = videoRef.current?.play();
        playPromise?.catch(() => {
          setVideoState(prev => ({ ...prev, isPlaying: false }));
        });
      }
    }

    setVideoState(prev => ({
      ...prev,
      currentTime: finalTime,
      isPlaying: shouldResumePlayback ? true : prev.isPlaying
    }));
  }, [cancelSmoothScrubPreviewFrame, seekPracticeMediaForScrub, setVideoState, videoRef]);

  useEffect(() => {
    return () => {
      cancelSmoothScrubPreviewFrame();
    };
  }, [cancelSmoothScrubPreviewFrame]);

  // YouTube-aware speed: enforces the adapter's supported-rate contract.
  // If YouTube rejects the requested rate, the UI state is not updated.
  const handleSetPlaybackRate = (rate: number) => {
    if (youtubeAdapterRef.current) {
      const result = youtubeAdapterRef.current.setSpeed(rate);
      if (result.ok) {
        setVideoState(prev => ({ ...prev, playbackRate: result.appliedRate }));
        flashSpeed(result.appliedRate);
      }
    } else {
      setVideoState(prev => ({ ...prev, playbackRate: rate }));
      flashSpeed(rate);
    }
  };

  // Briefly shows the new speed (e.g. "0.75x") over the video.
  const flashSpeed = (rate: number) => {
    if (speedFlashTimeoutRef.current !== null) {
      window.clearTimeout(speedFlashTimeoutRef.current);
    }
    setSpeedFlash({ text: formatSpeedValue(rate), key: Date.now() });
    speedFlashTimeoutRef.current = window.setTimeout(() => {
      setSpeedFlash(null);
      speedFlashTimeoutRef.current = null;
    }, SPEED_FLASH_MS);
  };

  // Up/down on the video or keyboard: the same step the speed control uses.
  const stepPlaybackSpeed = (direction: -1 | 1) => {
    const currentRate = videoState.playbackRate;
    const nextRate = youtubePractice
      ? getPresetStepperRate(currentRate, direction, YOUTUBE_SPEED_PRESETS)
      : stepLocalSpeed(currentRate, direction);
    if (Math.abs(nextRate - currentRate) < 0.0001) {
      // Already at the limit: still show the speed so the press is acknowledged.
      flashSpeed(currentRate);
      return;
    }
    applyPlaybackRate(nextRate);
  };

  // Sets the speed on the player (YouTube adapter or the <video> element) and in state.
  const applyPlaybackRate = (rate: number) => {
    handleSetPlaybackRate(rate);
    if (!youtubeAdapterRef.current && videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  };

  // Load share data when video is loaded from library:
  useEffect(() => {
    if (!sharingEnabled) {
      setCurrentShareData(null);
      return;
    }

    const loadCurrentShareData = async () => {
      if (library.currentVideoId && userProfile && !isDemo) {
        try {
          console.log('🔍 Loading share data for current video:', library.currentVideoId);
          const share = await getActiveShareForVideo(userProfile.uid, library.currentVideoId);
          setCurrentShareData(share);
          
          if (share) {
            console.log('✅ Found active share:', share.shareId);
          } else {
            console.log('ℹ️ No active share for this video');
          }
        } catch (error) {
          console.error('❌ Failed to load share data:', error);
          setCurrentShareData(null);
        }
      } else {
        setCurrentShareData(null);
      }
    };

    loadCurrentShareData();
  }, [library.currentVideoId, userProfile, isDemo, sharingEnabled]);

  // Handle create share
  const handleCreateShare = async () => {
    if (!sharingEnabled) return;

    if (!library.currentVideoId || !userProfile || !library.currentStoragePath) {
      alert('Please save the video to your library first.');
      return;
    }

    setIsCreatingShare(true);
    
    try {
      console.log('🟡 Creating share session...');
      
      const settings = FileUploadService.createSettingsFromState(videoState, segmentState);
      
      const result = await createSharedSession(
        library.currentVideoId,
        library.currentStoragePath,
        library.videoTitle || videoState.videoFileName,
        segmentState.segments,
        settings,
        userProfile.uid
      );
      
      // Update library metadata through the persistence module. Sharing remains
      // capability-gated and quarantined outside dev/internal modes.
      if (library.loadedLibraryProject) {
        await markLibraryProjectShared(library.loadedLibraryProject, result);
      }
      
      // Reload share data
      const share = await getActiveShareForVideo(userProfile.uid, library.currentVideoId);
      setCurrentShareData(share);
      
      console.log('🟢 Share created successfully:', result.shareUrl);
    } catch (error) {
      console.error('🔴 Share creation failed:', error);
      alert('Failed to create share: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsCreatingShare(false);
    }
  };

  // Handle revoke share
  const handleRevokeShare = async () => {
    if (!sharingEnabled) return;
    if (!currentShareData || !userProfile || !library.currentVideoId) return;
    
    try {
      console.log('🟡 Revoking share...');
      
      await deactivateShare(currentShareData.shareId, 'user_revoked');
      
      // Update library metadata through the persistence module. Sharing remains
      // capability-gated and quarantined outside dev/internal modes.
      if (library.loadedLibraryProject) {
        await clearLibraryProjectShare(library.loadedLibraryProject);
      }
      
      setCurrentShareData(null);
      setShowShareModal(false);
      console.log('🟢 Share revoked successfully');
    } catch (error) {
      console.error('🔴 Share revocation failed:', error);
      alert('Failed to revoke share: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Get share button text
  const getShareButtonText = () => {
    if (currentShareData) {
      const viewsRemaining = calculateViewsRemaining(currentShareData.accessCount, currentShareData.maxViews);
      const daysRemaining = calculateDaysRemaining(currentShareData.expiresAt);
      return `📊 ${viewsRemaining}v • ${daysRemaining}d`;
    }
    return 'Share Video';
  };

  // Update current time when timeupdate event fires
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;

    setVideoState(prev => ({ ...prev, currentTime: v.currentTime }));

    if (isTimelineScrubbingRef.current) return;

    const activeSegment = segmentState.activeSegment
      ? playbackSegments.find(segment => segment.id === segmentState.activeSegment) ?? null
      : null;

    if (segmentState.isLooping && activeSegment?.isDisabled) {
      setSegmentState(prev => ({
        ...prev,
        isLooping: false,
        activeSegment: prev.activeSegment === activeSegment.id ? null : prev.activeSegment
      }));
      return;
    }

    if (!segmentState.isLooping && !v.paused) {
      const safeDuration = videoState.duration > 0
        ? videoState.duration
        : (Number.isFinite(v.duration) && v.duration > 0 ? v.duration : v.currentTime);
      const skipTarget = getDisabledSegmentSkipTarget(v.currentTime, safeDuration, playbackSegments);

      if (skipTarget !== null && Math.abs(skipTarget - v.currentTime) > 0.001) {
        v.currentTime = skipTarget;
        setVideoState(prev => ({ ...prev, currentTime: skipTarget }));
        return;
      }
    }

    const basePlaybackRate = videoState.playbackRate;
    const isSmoothLoopPlayback =
      segmentState.isLooping &&
      segmentState.transitionType === 'smooth' &&
      !!activeSegment;

    if (!isSmoothLoopPlayback && Math.abs(v.playbackRate - basePlaybackRate) > 0.001) {
      v.playbackRate = basePlaybackRate;
    }

    if (!segmentState.isLooping || !activeSegment) return;
    const segment = activeSegment;

    const loopStart = getLoopStartTime(segment);

    const loopCueTime = segment.endTime - segment.transitionDuration;

    if (v.currentTime >= loopCueTime) {
      if (segmentState.transitionType === 'smooth' && segment.transitionDuration > 0) {
        handleSmoothTransition(v, segment, videoState.playbackRate);
      }
      if (v.currentTime >= segment.endTime) {
        v.currentTime = loopStart; // land on preroll before current segment
        v.playbackRate = basePlaybackRate;
      }
    }
  };



  // End segment function
  const endSegment = () => {
    if (shouldShowOrientationCheck) return;

    const isYouTubeMode = Boolean(youtubeAdapterRef.current);
    const hasVideoSource = isYouTubeMode || Boolean(videoRef.current);
    if (!hasVideoSource) return;

    const currentVideoTime = isYouTubeMode
      ? videoState.currentTime
      : (videoRef.current?.currentTime ?? videoState.currentTime);

    if (isBoundaryEditorOpen) {
      const draftUpdate = addBoundaryDraftSegmentAtTime(
        boundaryDraftSegments,
        currentVideoTime,
        segmentState.transitionDuration
      );

      if (!draftUpdate.addedSegment) return;

      const addedSegmentId = draftUpdate.addedSegment.id;

      recordBoundaryDraftSnapshot();
      setBoundaryDraftSegments(draftUpdate.segments);
      setHasBoundaryDraftChanges(true);
      setSegmentState(prev => ({
        ...prev,
        activeSegment: addedSegmentId,
        isLooping: false,
        isMarkingSegment: false,
        currentSegmentStart: null
      }));
      return;
    }

    const sourceSegments = getSourceSegments(segmentState.segments);
    const usedColors = sourceSegments.map(s => s.color);

    if (sourceSegments.length === 0) {
      // First segment - start from beginning, then keep boundary handles live
      // while the user continues building the initial segment set.
      const draftUpdate = addBoundaryDraftSegmentAtTime(
        [],
        currentVideoTime,
        segmentState.transitionDuration
      );

      if (!draftUpdate.addedSegment) return;

      const newSegment = draftUpdate.addedSegment;

      commitSegmentState(prev => ({
        ...prev,
        segments: draftUpdate.segments,
        activeSegment: newSegment.id,
        activeComboId: null,
        currentSegmentStart: null,
        isMarkingSegment: false,
        isLooping: false
      }));
      setBoundaryDraftSegments(draftUpdate.segments);
      setBoundaryDraftUndoStack([]);
      setHasBoundaryDraftChanges(true);
      setIsBoundaryEditorOpen(true);
    }
    else if (segmentState.isMarkingSegment && segmentState.currentSegmentStart !== null) {
      // End current segment and start next one
      const startTime = segmentState.currentSegmentStart;
      const endTime = currentVideoTime;

      if (endTime > startTime) {
        const newSegment = createSegment(
          sourceSegments.length + 1,
          startTime,
          endTime,
          usedColors,
          segmentState.transitionDuration
        );

        commitSegmentState(prev => ({
          ...prev,
          segments: [...prev.segments, newSegment],
          activeSegment: newSegment.id,
          currentSegmentStart: endTime
        }));
      }
    }
    else if (!segmentState.isMarkingSegment && sourceSegments.length > 0) {
      // Not currently marking - start a new segment from the end of the last segment
      const sortedSegments = [...sourceSegments].sort((a, b) => a.endTime - b.endTime);
      const lastSegment = sortedSegments[sortedSegments.length - 1];

      const newStartTime = Math.max(0, lastSegment.endTime);

      commitSegmentState(prev => ({
        ...prev,
        currentSegmentStart: newStartTime,
        isMarkingSegment: true
      }));

      // Seek to the start of the new segment so user can see where they're starting from
      if (isYouTubeMode) {
        youtubeAdapterRef.current?.seek(newStartTime);
      } else if (videoRef.current) {
        videoRef.current.currentTime = newStartTime;
      }
    }
  };

  // Tick/Clear all rows in the active Studio tab.
  const handleToggleAllLearned = (learned: boolean, tab: StudioTab) => {
    setSegmentState(prev => ({
      ...prev,
      segments: prev.segments.map(segment =>
        isStudioTabSegment(segment, tab)
          ? { ...segment, isLearned: learned }
          : segment
      )
    }));
  };

  const handleBatchToggleDisabledSelectedSegments = (tab: StudioTab) => {
    const selectedSegmentIds = segmentState.segments
      .filter(segment => isStudioTabSegment(segment, tab) && Boolean(segment.isLearned))
      .map(segment => segment.id);

    if (selectedSegmentIds.length === 0) {
      alert(`Select at least one ${tab === 'segments' ? 'segment' : 'combo'} first.`);
      return;
    }

    const selectedSet = new Set(selectedSegmentIds);

    commitSegmentState(prev => {
      const selectedSegments = prev.segments.filter(segment => selectedSet.has(segment.id));
      const shouldDisableSelected = selectedSegments.some(segment => !segment.isDisabled);
      const turnedOffActiveSegment =
        shouldDisableSelected &&
        !!prev.activeSegment &&
        selectedSet.has(prev.activeSegment);

      return {
        ...prev,
        segments: prev.segments.map(segment =>
          selectedSet.has(segment.id)
            ? { ...segment, isDisabled: shouldDisableSelected }
            : segment
        ),
        activeSegment: turnedOffActiveSegment ? null : prev.activeSegment,
        isLooping: turnedOffActiveSegment ? false : prev.isLooping
      };
    });
  };

  const handleBatchDeleteSelectedSegments = (tab: StudioTab) => {
    const selectedSegmentIds = segmentState.segments
      .filter(segment => isStudioTabSegment(segment, tab) && Boolean(segment.isLearned))
      .map(segment => segment.id);

    if (selectedSegmentIds.length === 0) {
      alert(`Select at least one ${tab === 'segments' ? 'segment' : 'combo'} first.`);
      return;
    }

    const selectedSet = new Set(selectedSegmentIds);

    commitSegmentState(prev => {
      const nextSegments = removeSegmentsAndRepairCombos(prev.segments, selectedSet);
      const activeSegmentStillExists = !!prev.activeSegment &&
        nextSegments.some(segment => segment.id === prev.activeSegment);
      const nextActiveSegment = activeSegmentStillExists ? prev.activeSegment : null;

      return {
        ...prev,
        segments: nextSegments,
        activeSegment: nextActiveSegment,
        activeComboId: resolveActiveComboId(nextSegments, nextActiveSegment, prev.activeComboId),
        isLooping: activeSegmentStillExists ? prev.isLooping : false
      };
    });
  };

  // Wrapper for saving a segment name
  const handleSaveSegmentName = (segmentId: string, newName: string) => {
  saveSegmentName(segmentId, newName);
  stopEditingSegment();
  };

  const handleToggleSegmentDisabled = (segmentId: string) => {
    commitSegmentState(prev => {
      const target = prev.segments.find(segment => segment.id === segmentId);
      if (!target) return prev;

      const shouldDisable = !target.isDisabled;
      const turnedOffActiveSegment = shouldDisable && prev.activeSegment === segmentId;

      return {
        ...prev,
        segments: prev.segments.map(segment =>
          segment.id === segmentId
            ? { ...segment, isDisabled: shouldDisable }
            : segment
        ),
        activeSegment: turnedOffActiveSegment ? null : prev.activeSegment,
        isLooping: turnedOffActiveSegment ? false : prev.isLooping
      };
    });
  };

  // The loop button (practice mode only: editing never loops).
  const handleToggleLoop = () => {
    if (isActionEditMode) return;
    toggleLoop();
  };

  // Combo left/right buttons in the control bar.
  const handleExtendCombo = (direction: 'left' | 'right') => {
    if (!extendComboFromActive(direction)) return;
    setStudioTab('combos');
    if (!showMergedTimeline) {
      toggleMergedTimeline();
    }
  };

  const handleCreateComboFromActiveSegment = () => {
    const didCreateCombo = createComboFromActiveSegment();
    if (!didCreateCombo) return;

    setStudioTab('combos');
    if (!showMergedTimeline) {
      toggleMergedTimeline();
    }
  };

  const handleDuplicateCombo = (comboId: string) => {
    const didDuplicateCombo = duplicateCombo(comboId);
    if (!didDuplicateCombo) return;

    setStudioTab('combos');
    if (!showMergedTimeline) {
      toggleMergedTimeline();
    }
  };

  const handleStudioComboClick = (comboId: string) => {
    if (!showMergedTimeline) {
      toggleMergedTimeline();
    }
    playSegment(comboId);
  };

  const handleUpdateSegmentsFromEditor = (updatedSegments: Segment[]) => {
      resetBoundaryEditorState();
      commitSegmentState(prev => {
        const normalizedSegments = normalizeCombos(renameGeneratedSourceSegmentsByTimeline(updatedSegments));
        const activeSegmentStillExists = !!prev.activeSegment &&
          normalizedSegments.some(segment => segment.id === prev.activeSegment);
        const nextActiveSegment = activeSegmentStillExists ? prev.activeSegment : null;

        return {
          ...prev,
          segments: normalizedSegments,
          activeSegment: nextActiveSegment,
          activeComboId: resolveActiveComboId(normalizedSegments, nextActiveSegment, prev.activeComboId),
          isLooping: activeSegmentStillExists ? prev.isLooping : false
        };
      });
    };

  // Load video from library
  const handleLoadFromLibrary = async (videoMeta: NormalizedLibraryProject, videoUrl?: string) => {
  try {
    resetBoundaryEditorState();
    setIsOrientationCheckOpen(false);
    invalidatePendingAutoSave();
    // Reset library state
    library.resetLibraryState();
    
    // Clean up previous video
    FileUploadService.cleanupVideoUrl(videoState.videoSrc);
    
    const rawSegments = videoMeta.segments || [];
    const normalizedSegments = normalizeCombos(rawSegments);
    const transitionMigration = migrateLegacyTransitionType(videoMeta.settings.transitionType);
    const loadedSegmentOverlapSeconds = resolveSegmentOverlapSeconds(videoMeta.settings, normalizedSegments);
    const loadedViewCrop = normalizeVideoViewCrop(videoMeta.settings.viewCrop);
    const loadedTimelineZoom = TIMELINE_ZOOM_LEVELS[
      getNearestTimelineZoomIndex(videoMeta.settings.timelineZoom ?? 1)
    ];
    // Reopen on the segment/combo that was playing when the project was last saved (like a
    // curated video's default combo). Ignored if it no longer exists or is hidden.
    const savedActiveSegmentId = videoMeta.settings.activeSegmentId ?? null;
    const restoredActiveSegment = savedActiveSegmentId
      ? normalizedSegments.find(segment => segment.id === savedActiveSegmentId && !segment.isDisabled) ?? null
      : null;
    const restoredActiveSegmentId = restoredActiveSegment?.id ?? null;
    const restoredActiveComboId = resolveActiveComboId(
      normalizedSegments,
      restoredActiveSegmentId,
      getFirstComboId(normalizedSegments)
    );
    pendingDefaultComboSeekRef.current = restoredActiveSegmentId;
    if (restoredActiveSegment) {
      setStudioTab(isCombo(restoredActiveSegment) ? 'combos' : 'segments');
    }
    const persistLoadMigrations = () => {
      const updates: Record<string, unknown> = {};
      if (transitionMigration.migratedFromLegacySmooth) {
        updates['settings.transitionType'] = transitionMigration.transitionType;
      }
      if (videoMeta.settings.segmentOverlapSeconds === undefined) {
        updates['settings.segmentOverlapSeconds'] = loadedSegmentOverlapSeconds;
      }
      if (normalizedSegments !== rawSegments) {
        updates.segments = normalizedSegments;
      }
      if (Object.keys(updates).length === 0) return;

      void persistLibraryProjectLoadMigrations(videoMeta, updates)
        .then(() => {
          console.log('🟢 Persisted load migrations for library item:', videoMeta.id);
        })
        .catch((migrationError) => {
          console.warn('⚠️ Failed to persist load migrations:', migrationError);
        });
    };

    if (isYouTubeLibraryProject(videoMeta)) {
      const youtubeSource = videoMeta.source;
      youtubeAdapterRef.current = null;
      isSeekingRef.current = false;

      setVideoState(prev => ({
        ...prev,
        videoSrc: null,
        videoFileName: videoMeta.title || youtubeSource.canonicalUrl,
        isLoading: false,
        loadingSource: null,
        playbackRate: getSupportedYouTubePlaybackRate(videoMeta.settings.defaultSpeed),
        isMirrored: videoMeta.settings.defaultMirror ?? true,
        isMuted: videoMeta.settings.defaultMuted ?? false,
        userMutePreference: videoMeta.settings.defaultMuted ?? false,
        currentTime: youtubeSource.startTime ?? 0,
        duration: 0,
        isPlaying: false,
        viewCrop: loadedViewCrop,
        timelineZoom: loadedTimelineZoom
      }));

      setSegmentState(prev => ({
        ...prev,
        segments: normalizedSegments,
        activeSegment: restoredActiveSegmentId,
        activeComboId: restoredActiveComboId,
        isMarkingSegment: false,
        currentSegmentStart: null,
        isLooping: videoMeta.settings.autoLoop || false,
        transitionType: transitionMigration.transitionType,
        segmentOverlap: loadedSegmentOverlapSeconds
      }));
      clearSegmentUndoHistory();

      setYoutubePractice(createYouTubePracticeRuntime(
        youtubeSource.videoId,
        youtubeSource.startTime,
        `library:${getLibraryProjectMembershipKey(videoMeta)}`
      ));

      persistLoadMigrations();
      library.setLoadedFromLibrary(videoMeta);
      setIsSegmentEditMode(false);
      setIsDemo(false);
      setOriginalVideoFile(null);
      console.log('🟢 YouTube project loaded from library:', videoMeta.title);
      return;
    }

    if (!videoUrl) {
      throw new Error('Uploaded video URL was not available.');
    }

    setYoutubePractice(null);
    youtubeAdapterRef.current = null;

    // Set video state using the callback form
    setVideoState(prev => ({
      ...prev,
      videoSrc: videoUrl,
      videoFileName: videoMeta.title,
      isLoading: true,
      loadingSource: 'firebase',
      playbackRate: videoMeta.settings.defaultSpeed || 1.0,
      isMirrored: videoMeta.settings.defaultMirror ?? true,
      userMutePreference: videoMeta.settings.defaultMuted ?? false,
      currentTime: 0,
      isPlaying: false,
      viewCrop: loadedViewCrop,
      timelineZoom: loadedTimelineZoom
    }));

    // Set segment state with loaded segments using callback form
    setSegmentState(prev => ({
      ...prev,
      segments: normalizedSegments,
      activeSegment: restoredActiveSegmentId,
      activeComboId: restoredActiveComboId,
      isMarkingSegment: false,
      currentSegmentStart: null,
      isLooping: videoMeta.settings.autoLoop || false,
      transitionType: transitionMigration.transitionType,
      segmentOverlap: loadedSegmentOverlapSeconds
    }));
    clearSegmentUndoHistory();

    persistLoadMigrations();
        
    // Mark as loaded from library (so it's already saved)
    library.setLoadedFromLibrary(videoMeta);
    setIsSegmentEditMode(false);
    
    setIsDemo(false);
    setOriginalVideoFile(null); // No original file when loading from library
    
    console.log('🟢 Video loaded from library:', videoMeta.title);
  } catch (error) {
    alert('Failed to load video: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
};
  

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    resetBoundaryEditorState();
    setIsOrientationCheckOpen(false);

    const success = FileUploadService.handleVideoFile(
      file,
      videoState.videoSrc,
      (updates) => setVideoState(prev => ({ ...prev, ...updates })),
      (updates) => setSegmentState(prev => ({ ...prev, ...updates })),
      (updates) => {
          if (updates.isDemo !== undefined) setIsDemo(updates.isDemo);},
      setOriginalVideoFile
    );

    if (success) {
      invalidatePendingAutoSave();
      clearSegmentUndoHistory();
      library.resetLibraryState(); // Reset library state
      setIsSegmentEditMode(true);
      setIsOrientationCheckOpen(true);
      setYoutubePractice(null);
      youtubeAdapterRef.current = null;
    }

    // If file was rejected, clear the input
    if (!success) {
      event.target.value = '';
    }
  };

  const triggerVideoFilePicker = useCallback(() => {
    videoFileInputRef.current?.click();
  }, []);
  
  // Handle timeline click
   const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    handleTimelineClickHelper(event, videoState.duration, seekToTimeActive);
  };

  const handleSegmentOverlapChange = useCallback((overlap: number) => {
    const nextOverlapSeconds = maxSegmentOverlapSeconds > 0
      ? clampSegmentOverlapSeconds(overlap, maxSegmentOverlapSeconds)
      : Math.max(0, Number.isFinite(overlap) ? overlap : DEFAULT_SEGMENT_OVERLAP_SECONDS);

    setSegmentState(prev => ({ ...prev, segmentOverlap: nextOverlapSeconds }));
  }, [maxSegmentOverlapSeconds, setSegmentState]);

  
  const getLoopStartTime = (segment: Segment) => {
    const overlapSeconds = Math.max(0, segmentState.segmentOverlap);
    return Math.max(0, segment.startTime - overlapSeconds);
  };

  // Play a specific segment
  const playSegment = (segmentId: string) => {
    if (shouldShowOrientationCheck) {
      return;
    }

    const segment = playbackSegments.find(s => s.id === segmentId);
    if (!segment || segment.isDisabled) return;

    // Editing never loops: playing a segment there just plays it through.
    activateSegmentForPlay(segmentId, !isActionEditMode);
    const loopStart = getLoopStartTime(segment);

    if (youtubeAdapterRef.current) {
      youtubeAdapterRef.current.seek(loopStart);
      youtubeAdapterRef.current.play();
      setVideoState(prevState => ({ ...prevState, isPlaying: true }));
    } else if (videoRef.current) {
      seekToTime(loopStart);
      videoRef.current.play();
      setVideoState(prevState => ({ ...prevState, isPlaying: true }));
    }
  };

  // Next/previous stays within the kind of loop that is playing: combo to combo while a combo is
  // active, otherwise segment to segment.
  const navigateToAdjacentSegment = (direction: 'previous' | 'next') => {
    const activeLoopSegment = segmentState.activeSegment
      ? playbackSegments.find(segment => segment.id === segmentState.activeSegment) ?? null
      : null;
    const navigateCombos = Boolean(activeLoopSegment && isCombo(activeLoopSegment));
    const orderedSegments = sortSegmentsByStartTime(
      playbackSegments.filter(segment => !segment.isDisabled && isCombo(segment) === navigateCombos)
    );
    if (orderedSegments.length === 0) return;

    const directionStep = direction === 'next' ? 1 : -1;
    const activeIndex = orderedSegments.findIndex(segment => segment.id === segmentState.activeSegment);

    let targetIndex = activeIndex;
    if (targetIndex === -1) {
      const currentTime = videoRef.current?.currentTime ?? videoState.currentTime;
      targetIndex = orderedSegments.findIndex(segment => currentTime < segment.endTime);
      if (targetIndex === -1) {
        targetIndex = orderedSegments.length - 1;
      }
    }

    const nextIndex = Math.min(
      orderedSegments.length - 1,
      Math.max(0, targetIndex + directionStep)
    );

    playSegment(orderedSegments[nextIndex].id);
  };

  // At the end of the video: replay the active loop if one is running, otherwise start the whole
  // video again from the beginning with the same speed/mirror/crop settings.
  const handleMediaEnded = () => {
    const activeLoopSegment = segmentState.isLooping && segmentState.activeSegment
      ? playbackSegments.find(segment => segment.id === segmentState.activeSegment && !segment.isDisabled)
      : undefined;
    const restartTime = activeLoopSegment ? getLoopStartTime(activeLoopSegment) : 0;

    if (youtubeAdapterRef.current) {
      youtubeAdapterRef.current.seek(restartTime);
      youtubeAdapterRef.current.play();
      setVideoState(prev => ({ ...prev, isPlaying: true, currentTime: restartTime }));
    } else if (videoRef.current) {
      seekToTime(restartTime);
      void videoRef.current.play();
      setVideoState(prev => ({ ...prev, isPlaying: true }));
    }
  };

  // Keeps the on-video controls visible for a moment after any interaction.
  const revealPracticeOverlay = useCallback(() => {
    setIsPracticeOverlayActive(true);
    if (practiceOverlayTimeoutRef.current !== null) {
      window.clearTimeout(practiceOverlayTimeoutRef.current);
    }
    practiceOverlayTimeoutRef.current = window.setTimeout(() => {
      setIsPracticeOverlayActive(false);
      practiceOverlayTimeoutRef.current = null;
    }, PRACTICE_OVERLAY_HIDE_MS);
  }, []);

  // Starting playback begins the fade-out countdown; pausing keeps the controls on screen.
  useEffect(() => {
    if (videoState.isPlaying) {
      revealPracticeOverlay();
    } else {
      if (practiceOverlayTimeoutRef.current !== null) {
        window.clearTimeout(practiceOverlayTimeoutRef.current);
        practiceOverlayTimeoutRef.current = null;
      }
      setIsPracticeOverlayActive(true);
    }
  }, [revealPracticeOverlay, videoState.isPlaying]);

  // Tap/click on the video plays or pauses straight away; a second tap within the double-tap window
  // also toggles fullscreen (the two play/pause toggles cancel out, so playback carries on).
  const lastSurfaceTapRef = useRef(0);
  const handlePracticeSurfaceClick = () => {
    revealPracticeOverlay();
    handleTogglePlay();
    const now = Date.now();
    if (now - lastSurfaceTapRef.current <= DOUBLE_TAP_FULLSCREEN_MS) {
      lastSurfaceTapRef.current = 0;
      toggleFullscreen();
      return;
    }
    lastSurfaceTapRef.current = now;
  };

  const handlePracticeOverlayAction = (
    event: React.MouseEvent<HTMLButtonElement>,
    action: () => void
  ) => {
    // Don't let the click reach the play/pause surface underneath.
    event.stopPropagation();
    // The first tap of a double-tap can reveal these buttons under the finger; a second tap landing
    // on one still counts as the double-tap (fullscreen) rather than as that button.
    if (Date.now() - lastSurfaceTapRef.current <= DOUBLE_TAP_FULLSCREEN_MS) {
      handlePracticeSurfaceClick();
      return;
    }
    revealPracticeOverlay();
    action();
  };

  const applyBoundaryDraftsToSegments = useCallback((segments: Segment[]): Segment[] => {
    return applyBoundaryDraftSegments(segments, boundaryDraftSegments);
  }, [boundaryDraftSegments]);

  const getSegmentStateForActionSave = useCallback((baseState: SegmentState): SegmentState => {
    if (!isBoundaryEditorOpen) return baseState;

    return {
      ...baseState,
      segments: applyBoundaryDraftsToSegments(baseState.segments)
    };
  }, [applyBoundaryDraftsToSegments, isBoundaryEditorOpen]);

  const boundaryEditableSegments = getBoundaryEditableSegments(segmentState.segments);
  const sourceSegmentsCount = boundaryEditableSegments.length;
  const isActionEditMode = isSegmentEditMode || isBoundaryEditorOpen || sourceSegmentsCount === 0;
  const canExtendComboLeft = !isBoundaryEditorOpen && getComboExtensionIds('left') !== null;
  // Latest loop target for the precise YouTube loop timer; any change cancels a pending jump.
  const youtubeLoopTarget = segmentState.activeSegment
    ? playbackSegments.find(segment => segment.id === segmentState.activeSegment) ?? null
    : null;
  youtubeLoopStateRef.current = {
    isLooping: segmentState.isLooping,
    activeSegmentId: segmentState.activeSegment,
    loopStart: youtubeLoopTarget
      ? Math.max(0, youtubeLoopTarget.startTime - Math.max(0, segmentState.segmentOverlap))
      : 0
  };
  useEffect(() => () => {
    if (youtubeLoopTimerRef.current !== null) {
      window.clearTimeout(youtubeLoopTimerRef.current);
      youtubeLoopTimerRef.current = null;
    }
  }, [segmentState.activeSegment, segmentState.isLooping, youtubePractice?.runtimeKey]);

  // Looping is not possible while editing, however edit mode was entered (new video, no segments,
  // boundary editor, or a combo picked from the list).
  useEffect(() => {
    if (isActionEditMode && segmentState.isLooping) {
      setSegmentState(prev => (prev.isLooping ? { ...prev, isLooping: false } : prev));
    }
  }, [isActionEditMode, segmentState.isLooping, setSegmentState]);
  const canExtendComboRight = !isBoundaryEditorOpen && getComboExtensionIds('right') !== null;
  const activeStudioTab = hasComboSegments ? studioTab : 'segments';
  const showStudioTabs = true;
  const canSaveLocal = isBoundaryEditorOpen
    ? boundaryDraftSegments.length > 0
    : segmentState.segments.length > 0;
  const canOpenBoundaryEditor = boundaryEditableSegments.length >= 1;
  const editableSegmentsForModeControls = isBoundaryEditorOpen
    ? boundaryDraftSegments
    : boundaryEditableSegments;
  const activeEditableSegmentForModeControls = segmentState.activeSegment
    ? editableSegmentsForModeControls.find(segment => segment.id === segmentState.activeSegment) ?? null
    : null;
  const segmentVisibilityTarget =
    activeEditableSegmentForModeControls ?? editableSegmentsForModeControls[0] ?? null;
  const canUndoBoundaryDraftChange = isBoundaryEditorOpen && boundaryDraftUndoStack.length > 0;
  const canUndoModeAction = isBoundaryEditorOpen
    ? canUndoBoundaryDraftChange || canUndoSegmentChange
    : canUndoSegmentChange;

  const closeActionMenus = () => undefined;

  const handleUndoModeAction = () => {
    if (isBoundaryEditorOpen && canUndoBoundaryDraftChange) {
      const previousDraftSegments = boundaryDraftUndoStack[boundaryDraftUndoStack.length - 1];
      if (!previousDraftSegments) return;

      const nextUndoStack = boundaryDraftUndoStack.slice(0, -1);
      const previousDraftSegmentIds = new Set(previousDraftSegments.map(segment => segment.id));

      setBoundaryDraftUndoStack(nextUndoStack);
      setBoundaryDraftSegments(cloneSegmentsForUndo(previousDraftSegments));
      setHasBoundaryDraftChanges(nextUndoStack.length > 0);
      setSegmentState(prev => {
        if (!prev.activeSegment || previousDraftSegmentIds.has(prev.activeSegment)) return prev;

        return {
          ...prev,
          activeSegment: previousDraftSegments[previousDraftSegments.length - 1]?.id ?? null,
          isLooping: false
        };
      });
      return;
    }

    if (isBoundaryEditorOpen) {
      resetBoundaryEditorState();
    }

    if (canUndoSegmentChange) {
      undoLastSegmentChange();
    }
  };

  const handleCycleComboLaneVisibility = () => {
    if (!hasComboSegments) return;

    if (!showMergedTimeline) {
      setShowAllCombos(false);
      toggleMergedTimeline();
      return;
    }

    if (!showAllCombos) {
      setShowAllCombos(true);
      return;
    }

    setShowAllCombos(false);
    toggleMergedTimeline();
  };

  const handleToggleModeControlSegmentVisibility = () => {
    if (!segmentVisibilityTarget) return;

    if (isBoundaryEditorOpen) {
      const shouldDisable = !segmentVisibilityTarget.isDisabled;
      recordBoundaryDraftSnapshot();
      setBoundaryDraftSegments(prev => prev.map(segment =>
        segment.id === segmentVisibilityTarget.id
          ? { ...segment, isDisabled: shouldDisable }
          : segment
      ));
      setHasBoundaryDraftChanges(true);

      if (shouldDisable && segmentState.activeSegment === segmentVisibilityTarget.id) {
        setSegmentState(prev => ({
          ...prev,
          activeSegment: null,
          isLooping: false
        }));
      }
      return;
    }

    handleToggleSegmentDisabled(segmentVisibilityTarget.id);
  };

  const resetToDefaultLoadingPage = useCallback(() => {
    resetBoundaryEditorState();
    invalidatePendingAutoSave();
    closeActionMenus();
    setShowLibrary(false);
    setPurchaseOpen(false);
    setShowShareModal(false);
    setShowSegmentEditor(false);
    setIsAdvancedControlsOpen(false);
    setTimelineZoomIndex(0);
    setIsCreatingShare(false);

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    FileUploadService.cleanupVideoUrl(videoState.videoSrc);

    setVideoState(prev => ({
      ...prev,
      videoSrc: null,
      videoFileName: '',
      isLoading: false,
      loadingSource: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackRate: 1.0,
      isMirrored: true,
      isMuted: true,
      userMutePreference: false,
      viewCrop: undefined
    }));

    setSegmentState(prev => ({
      ...prev,
      segments: [],
      activeSegment: null,
      activeComboId: null,
      isMarkingSegment: false,
      currentSegmentStart: null,
      isLooping: false,
      transitionType: 'none',
      transitionDuration: 0.5,
      segmentOverlap: DEFAULT_SEGMENT_OVERLAP_SECONDS
    }));
    clearSegmentUndoHistory();

    setIsDemo(false);
    setIsSegmentEditMode(false);
    setOriginalVideoFile(null);
    setCurrentShareData(null);
    setIsOrientationCheckOpen(false);
    setYoutubePractice(null);
    youtubeAdapterRef.current = null;
    library.resetLibraryState();

    if (videoFileInputRef.current) {
      videoFileInputRef.current.value = '';
    }
  }, [
    library,
    invalidatePendingAutoSave,
    clearSegmentUndoHistory,
    resetBoundaryEditorState,
    setSegmentState,
    setVideoState,
    setTimelineZoomIndex,
    setIsDemo,
    videoRef,
    videoState.videoSrc
  ]);

  const handleBrandActivation = useCallback(() => {
    setBrandAnimationKey(currentKey => currentKey + 1);
    resetToDefaultLoadingPage();
  }, [resetToDefaultLoadingPage]);

  const handleSaveAndExitSegmentEditMode = () => {
    const stateForSave = getSegmentStateForActionSave(segmentState);
    const hasSegmentsToSave = stateForSave.segments.length > 0;
    if (!hasSegmentsToSave) return;

    if (isBoundaryEditorOpen) {
      commitSegmentState(stateForSave);
      resetBoundaryEditorState();
    }

    if (library.autoSaveStatus === 'saving') {
      closeActionMenus();
      setIsSegmentEditMode(false);
      return;
    }

    if (library.canUpdateSegments) {
      library.handleUpdateSegments(videoState, stateForSave);
      closeActionMenus();
      setIsSegmentEditMode(false);
      return;
    }

    if (youtubePractice) {
      library.handleSaveYouTubeProject(youtubePractice, videoState, stateForSave);
      closeActionMenus();
      setIsSegmentEditMode(false);
      return;
    }

    if (originalVideoFile && library.canSaveToLibrary) {
      library.handleSaveToLibrary(originalVideoFile, videoState, stateForSave);
      closeActionMenus();
      setIsSegmentEditMode(false);
      return;
    }

    const settings = FileUploadService.createSettingsFromState(videoState, stateForSave);
    saveCheckpoints(stateForSave.segments, videoState.videoFileName, settings);
    closeActionMenus();
    setIsSegmentEditMode(false);
  };

  const handleEnterSegmentEditMode = () => {
    closeActionMenus();
    setIsSegmentEditMode(true);
    setStudioTab('segments');
    setSegmentState(prev => ({ ...prev, isLooping: false }));

    if (canOpenBoundaryEditor) {
      handleOpenBoundaryEditor();
    } else {
      resetBoundaryEditorState();
    }
  };

  const handleBoundaryDraftChange = (updatedSegments: Segment[]) => {
    recordBoundaryDraftSnapshot();
    setBoundaryDraftSegments(renameGeneratedSourceSegmentsByTimeline(sortSegmentsByStartTime(updatedSegments)));
    setHasBoundaryDraftChanges(true);
  };

  const handleOpenBoundaryEditor = () => {
    if (!canOpenBoundaryEditor) {
      alert('Create at least one source segment to edit boundaries.');
      return;
    }

    if (segmentState.activeSegment && !boundaryEditableSegments.some(segment => segment.id === segmentState.activeSegment)) {
      setActiveSegment(null);
    }
    setBoundaryDraftSegments(boundaryEditableSegments.map(segment => ({ ...segment })));
    setBoundaryDraftUndoStack([]);
    setHasBoundaryDraftChanges(false);
    setIsBoundaryEditorOpen(true);
    closeActionMenus();
  };

  // Handle checkpoints load
  const handleLoadCheckpoints = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!videoRef.current) {
      alert('No video element available yet.');
      return;
    }

    try {
      resetBoundaryEditorState();
      await FileUploadService.handleCheckpointLoad(
        file,
        (updates) => setSegmentState(prev => ({ ...prev, ...updates })),
        (updates) => setVideoState(prev => ({ ...prev, ...updates })),
        videoRef.current // ✅ the actual HTMLVideoElement
      );
      clearSegmentUndoHistory();
      setIsSegmentEditMode(true);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to load checkpoints');
    }
  };

  const handleVideoLoadError = () => {
    resetBoundaryEditorState();
    const failedVideoName = videoState.videoFileName || 'video';
    console.error('🔴 Video load failed:', failedVideoName, videoState.videoSrc);

    FileUploadService.cleanupVideoUrl(videoState.videoSrc);
    handleVideoError();

    setVideoState(prev => ({
      ...prev,
      videoSrc: null,
      videoFileName: '',
      loadingSource: null,
      currentTime: 0,
      duration: 0,
      viewCrop: undefined
    }));

    setSegmentState(prev => ({
      ...prev,
      segments: [],
      activeSegment: null,
      activeComboId: null,
      isMarkingSegment: false,
      currentSegmentStart: null,
      isLooping: false,
      transitionType: 'none'
    }));
    clearSegmentUndoHistory();

    setOriginalVideoFile(null);
    setCurrentShareData(null);
    setIsSegmentEditMode(false);
    setTimelineZoomIndex(0);
    invalidatePendingAutoSave();
    library.resetLibraryState();

    alert(
      `Failed to load "${failedVideoName}".\n\n` +
      describeVideoLoadFailure(failedVideoName)
    );
  };

  
  // Container and layout styles
  // On phones the studio panel floats over the video instead of taking a grid column.
  const containerStyle = getContainerStyle(isFullscreen, practiceMode || isMobileFullscreenSidebarMode);
  const videoContainerStyle = getVideoContainerStyle(isFullscreen);
  const controlsStyle = getControlsStyle(isFullscreen);
  const PASTEL_SEGMENT_COLOR = SOURCE_SEGMENT_COLOR;
  const PASTEL_SEGMENT_HOVER_COLOR = '#4F398E';
  const MERGED_ACTIVE_COLOR = COMBO_SEGMENT_COLOR;
  const MUTE_ACTIVE_COLOR = '#C75B5B';
  const SAVE_ACTION_COLOR = '#0f9488';
  const LOOP_HERO_COLOR = SOURCE_SEGMENT_COLOR;
  const SAVE_ACTION_HOVER_COLOR = '#0b7a70';
  const forceSingleControlRow = !isFullscreen && isMobileControlsViewport;
  const estimatedSidebarWidth = isFullscreen && !practiceMode ? 360 : 0;
  const effectiveControlsWidth = Math.max(0, controlsContainerWidth - estimatedSidebarWidth);
  const isCompactPrimaryControls = effectiveControlsWidth > 0 && effectiveControlsWidth < 1120;
  const isUltraCompactPrimaryControls = effectiveControlsWidth > 0 && effectiveControlsWidth < 960;
  const isNanoCompactPrimaryControls = effectiveControlsWidth > 0 && effectiveControlsWidth < 840;
  const isPhonePrimaryControls = effectiveControlsWidth > 0 && effectiveControlsWidth < 430;
  const isPocketPrimaryControls = effectiveControlsWidth > 0 && effectiveControlsWidth < 340;
  const enabledPlaybackSegments = playbackSegments.filter(segment => !segment.isDisabled);
  const playingSourceSegmentId = getSourceSegments(playbackSegments).find(segment => (
    videoState.currentTime >= segment.startTime && videoState.currentTime < segment.endTime
  ))?.id ?? null;
  const adjacentSegmentNavigationDisabled = enabledPlaybackSegments.length === 0;
  const hasSegmentableVideoSource = Boolean(videoState.videoSrc || youtubePractice);
  const canEndSegment = hasSegmentableVideoSource && !shouldShowOrientationCheck && (isBoundaryEditorOpen || !segmentState.isLooping);
  const segmentActionTitle = isBoundaryEditorOpen
    ? 'Add or split a segment at the current playhead'
    : shouldShowOrientationCheck
    ? 'Confirm direction before creating segments'
    : segmentState.isLooping
    ? 'Turn loop off to create more segments'
    : 'Mark end of current segment';
  const activeLoopTarget = segmentState.activeSegment
    ? playbackSegments.find(segment => segment.id === segmentState.activeSegment) ?? null
    : null;
  const activeLoopComboRange = activeLoopTarget && isMergedSegment(activeLoopTarget)
    ? getComboRange(playbackSegments, activeLoopTarget)
    : null;
  const muteControlTitle = segmentState.isLooping
    ? videoState.isMuted
      ? 'Unmute (Practice with sound)'
      : 'Mute (Practice silently)'
    : videoState.isMuted
    ? 'Unmute'
    : 'Mute';
  let controlButtonSize = 44;
  if (isCompactPrimaryControls) controlButtonSize = 38;
  if (isUltraCompactPrimaryControls) controlButtonSize = 34;
  if (isNanoCompactPrimaryControls) controlButtonSize = 30;
  if (forceSingleControlRow) controlButtonSize = Math.min(controlButtonSize, 30);
  const fullscreenControlStackBudgetPx = isFullscreen && viewportHeight > 0
    ? viewportHeight / 5
    : null;
  const windowedBudgetHeightPx = !isFullscreen
    ? videoStageHeight > 0 && viewportHeight > 0
      ? Math.min(videoStageHeight, viewportHeight)
      : videoStageHeight > 0
      ? videoStageHeight
      : viewportHeight
    : 0;
  const windowedControlStackBudgetPx = windowedBudgetHeightPx > 0
    ? windowedBudgetHeightPx / 5
    : null;
  if (fullscreenControlStackBudgetPx !== null) {
    controlButtonSize = Math.min(
      controlButtonSize,
      Math.max(24, Math.floor(fullscreenControlStackBudgetPx * 0.27))
    );
  }
  if (windowedControlStackBudgetPx !== null) {
    controlButtonSize = Math.min(
      controlButtonSize,
      Math.max(26, Math.floor(windowedControlStackBudgetPx * 0.34))
    );
  }
  const controlIconSize = controlButtonSize <= 30 ? 14 : controlButtonSize <= 34 ? 16 : UI_DIMENSIONS.ICON_SIZE;
  const controlsSurfaceStyle = isFullscreen
    ? ({
        ...controlsStyle,
        '--fullscreen-control-stack-budget': fullscreenControlStackBudgetPx
          ? `${fullscreenControlStackBudgetPx}px`
          : '20vh',
        '--fullscreen-control-row-height': `${controlButtonSize}px`
      } as React.CSSProperties & Record<`--${string}`, string>)
    : windowedControlStackBudgetPx !== null
    ? ({
        ...controlsStyle,
        '--windowed-control-stack-budget': `${windowedControlStackBudgetPx}px`,
        '--windowed-control-row-height': `${controlButtonSize}px`
      } as React.CSSProperties & Record<`--${string}`, string>)
    : controlsStyle;
  const videoEdgeNavIconSize = isFullscreen ? 40 : 34;
  const videoOverlayPlayIconSize = isFullscreen ? 44 : 36;
  const utilityIconSize = 16;
  const timelineZoomLevel = TIMELINE_ZOOM_LEVELS[timelineZoomIndex] ?? 1;
  const canZoomTimelineOut = timelineZoomIndex > 0;
  const canZoomTimelineIn = timelineZoomIndex < TIMELINE_ZOOM_LEVELS.length - 1;
  const modeControlIconSize = controlButtonSize <= 30 ? 22 : isFullscreen ? 28 : 26;
  const comboLaneModeControlTitle = !hasComboSegments
    ? 'Create a combo first'
    : !showMergedTimeline
    ? 'Show combo lane'
    : !showAllCombos
    ? 'Show all combos'
    : 'Hide combo lane';
  const segmentVisibilityControlTitle = !segmentVisibilityTarget
    ? 'Create a segment first'
    : segmentVisibilityTarget.isDisabled
    ? 'Show segment'
    : 'Hide segment';
  const savePracticeControlTitle = !canSaveLocal
    ? 'Create segments first'
    : !isAuthenticated
    ? 'Save locally and start practice'
    : library.canUpdateSegments
    ? 'Save changes to library and start practice'
    : 'Save segments and practice';

  // Bubble-free transport / advanced icon buttons (see combo-preview.html prototype):
  // plain icons on the control panel, no filled pill. Active state is conveyed
  // through icon colour, with a light pressed-state shadow on select controls.
  const restIconColor = isFullscreen ? '#e5e7eb' : '#475569';
  const disabledIconColor = isFullscreen ? 'rgba(226, 232, 240, 0.35)' : '#b6bfca';
  const barIconBaseStyle: React.CSSProperties = {
    width: `${controlButtonSize}px`,
    minWidth: `${controlButtonSize}px`,
    height: `${controlButtonSize}px`,
    padding: 0,
    border: 'none',
    background: 'transparent',
    borderRadius: '0.6rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease'
  };

  const controlIconStyle = (iconColor: string = restIconColor): React.CSSProperties => ({
    ...barIconBaseStyle,
    color: iconColor,
    cursor: 'pointer'
  });

  const secondaryIconStyle = (disabled: boolean): React.CSSProperties => ({
    ...barIconBaseStyle,
    color: disabled ? disabledIconColor : restIconColor,
    cursor: disabled ? 'not-allowed' : 'pointer'
  });
  const showAdvancedControlsRow = isAdvancedControlsOpen;
  const advancedControlsTitle = isAdvancedControlsOpen ? 'Hide advanced controls' : 'Show advanced controls';
  const advancedToggleStyle: React.CSSProperties = {
    ...barIconBaseStyle,
    color: isAdvancedControlsOpen ? MERGED_ACTIVE_COLOR : restIconColor,
    cursor: 'pointer'
  };
  const appliedViewCrop = normalizeVideoViewCrop(videoState.viewCrop);
  const hasViewCrop = Boolean(appliedViewCrop);
  const renderedViewCrop = isCropEditorOpen ? undefined : appliedViewCrop;
  const cropEditorAspectRatio = youtubePractice ? 16 / 9 : uploadedVideoAspectRatio;
  // The stage is sized to the picture instead of to a fixed height budget, so `contain` is correct in
  // every mode: uncropped media already fills a box that matches its aspect ratio, and an applied crop
  // requires it because getVideoCropMediaStyle's inset/translate percentages assume the picture fills
  // the box at source aspect ratio, which object-fit: cover would rescale out from under it.
  const mediaFitMode: VideoCropFitMode = 'contain';
  const cropViewButtonStyle = controlIconStyle(hasViewCrop ? MERGED_ACTIVE_COLOR : restIconColor);
  const activeTimelineSegmentIsSource = Boolean(
    segmentState.activeSegment &&
    boundaryEditableSegments.some(segment => segment.id === segmentState.activeSegment)
  );
  const timelineDeckHasComboLane = !isBoundaryEditorOpen && showMergedTimeline && hasComboSegments && (
    showAllCombos ||
    Boolean(segmentState.activeComboId) ||
    activeTimelineSegmentIsSource
  );
  const timelineDeckLaneClassName = timelineDeckHasComboLane
    ? 'player-timeline-deck-has-combo-lane'
    : 'player-timeline-deck-single-lane';
  const effectiveTimelineLanePlacement: TimelineLanePlacement = isFullscreen
    ? 'over-video'
    : 'under-video';
  const playerStageControlStackClassName = [
    'player-stage-control-stack',
    `timeline-lane-placement-${effectiveTimelineLanePlacement}`,
    timelineDeckLaneClassName
  ].join(' ');
  const playerControlsShellClassName = [
    'fullscreen-controls',
    'player-controls-shell',
    'control-surface',
    `control-surface-${effectiveTimelineLanePlacement}`,
    isFullscreen ? 'control-surface-fullscreen' : '',
    !isFullscreen && windowedControlStackBudgetPx !== null ? 'control-surface-windowed-budgeted' : '',
    isFullscreen && !isFullscreenControlsVisible && !isAdvancedControlsOpen ? 'control-surface-hidden' : '',
    isFullscreen && isAdvancedControlsOpen ? 'control-surface-advanced-open' : ''
  ].filter(Boolean).join(' ');
  // Bubble-free labelled controls (Mirror) and icon-only advanced actions: plain controls with no
  // fill or shadow, matching the prototype's contour-free .adv-btn. State reads through colour.
  const mirrorAccentColor = isFullscreen ? '#f2c14e' : '#a16207';
  const flatLabelStyle = (color: string): React.CSSProperties => ({
    height: `${controlButtonSize}px`,
    minHeight: `${controlButtonSize}px`,
    padding: controlButtonSize <= 30 ? '0 8px' : '0 11px',
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    borderRadius: '0.6rem',
    color,
    display: 'inline-flex',
    alignItems: 'center',
    gap: controlButtonSize <= 30 ? '5px' : '6px',
    fontSize: controlButtonSize <= 30 ? '0.74rem' : '0.84rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  });
  const toggleAdvancedControls = () => {
    setIsAdvancedControlsOpen(prev => !prev);
    if (isFullscreen) revealFullscreenControls();
  };
  const restoreInitialCropDraft = useCallback(() => {
    const initialViewCrop = cropEditorInitialViewCropRef.current;
    setDraftViewCrop(initialViewCrop ?? DEFAULT_VIDEO_VIEW_CROP_SELECTION);
    setVideoState(prev => ({
      ...prev,
      viewCrop: initialViewCrop
    }));
  }, [setVideoState]);
  const handleToggleCropEditor = useCallback(() => {
    if (isCropEditorOpen) {
      restoreInitialCropDraft();
      setIsCropEditorOpen(false);
    } else {
      const initialViewCrop = normalizeVideoViewCrop(videoState.viewCrop);
      cropEditorInitialViewCropRef.current = initialViewCrop;
      setDraftViewCrop(initialViewCrop ?? DEFAULT_VIDEO_VIEW_CROP_SELECTION);
      setIsCropEditorOpen(true);
    }
    if (isFullscreen) revealFullscreenControls();
  }, [isCropEditorOpen, isFullscreen, restoreInitialCropDraft, revealFullscreenControls, videoState.viewCrop]);
  const handleApplyViewCrop = useCallback(() => {
    const nextViewCrop = normalizeVideoViewCrop(draftViewCrop);
    setVideoState(prev => ({
      ...prev,
      viewCrop: nextViewCrop
    }));
    cropEditorInitialViewCropRef.current = nextViewCrop;
    setIsCropEditorOpen(false);
    if (isFullscreen) revealFullscreenControls();
  }, [draftViewCrop, isFullscreen, revealFullscreenControls, setVideoState]);
  const handleCancelCropEditor = useCallback(() => {
    restoreInitialCropDraft();
    setIsCropEditorOpen(false);
    if (isFullscreen) revealFullscreenControls();
  }, [isFullscreen, restoreInitialCropDraft, revealFullscreenControls]);
  const handleResetViewCrop = useCallback(() => {
    setDraftViewCrop(FULL_FRAME_VIDEO_VIEW_CROP);
    setVideoState(prev => ({
      ...prev,
      viewCrop: undefined
    }));
    if (isFullscreen) revealFullscreenControls();
  }, [isFullscreen, revealFullscreenControls, setVideoState]);
  const handleTimelineZoomIn = () => {
    setTimelineZoomIndex(index => Math.min(TIMELINE_ZOOM_LEVELS.length - 1, index + 1));
  };
  const handleTimelineZoomOut = () => {
    setTimelineZoomIndex(index => Math.max(0, index - 1));
  };

  const handleSidebarSegmentClick = (segmentId: string) => {
    playSegment(segmentId);
    if (isMobileFullscreenSidebarMode) {
      setIsFullscreenSegmentsPanelOpen(false);
    }
  };

  const handleSidebarComboClick = (comboId: string) => {
    handleStudioComboClick(comboId);
    if (isMobileFullscreenSidebarMode) {
      setIsFullscreenSegmentsPanelOpen(false);
    }
  };
  const activeLoopIsCombo = Boolean(
    segmentState.activeSegment &&
    playbackSegments.some(segment => segment.id === segmentState.activeSegment && isCombo(segment))
  );
  const navigationUnitLabel = activeLoopIsCombo ? 'combo' : 'segment';
  const isPracticeOverlayVisible = !videoState.isPlaying || isPracticeOverlayActive;

  // Centred on-video controls: tap/click anywhere = play/pause, left/right = previous/next
  // segment (or combo), up/down = speed. They fade out while playing and come back on interaction.
  const renderPracticeOverlay = () => (shouldShowOrientationCheck || isCropEditorOpen) ? null : (
    <div
      className={`video-practice-overlay ${isPracticeOverlayVisible ? 'is-visible' : 'is-idle'}`}
      data-testid="video-practice-overlay"
      onClick={handlePracticeSurfaceClick}
      onPointerMove={(event) => {
        if (event.pointerType === 'mouse') revealPracticeOverlay();
      }}
    >
      <button
        type="button"
        className="video-practice-overlay-button video-practice-overlay-play"
        onClick={(event) => {
          // Same as tapping the video itself, so a double-tap on the button also goes fullscreen.
          event.stopPropagation();
          handlePracticeSurfaceClick();
        }}
        aria-label={videoState.isPlaying ? 'Pause (Space)' : 'Play (Space)'}
        title={`${videoState.isPlaying ? 'Pause' : 'Play'} (Space) · double-tap the video for full screen (F)`}
        tabIndex={isPracticeOverlayVisible ? 0 : -1}
      >
        {videoState.isPlaying
          ? <Pause size={videoOverlayPlayIconSize} fill="currentColor" strokeWidth={1.6} />
          : <Play size={videoOverlayPlayIconSize} fill="currentColor" strokeWidth={1.6} />}
      </button>
      <button
        type="button"
        className="video-practice-overlay-button video-practice-overlay-prev"
        onClick={(event) => handlePracticeOverlayAction(event, () => navigateToAdjacentSegment('previous'))}
        disabled={adjacentSegmentNavigationDisabled}
        aria-label={`Previous ${navigationUnitLabel} (Left arrow)`}
        title={`Previous ${navigationUnitLabel} (←)`}
        tabIndex={isPracticeOverlayVisible ? 0 : -1}
      >
        <ChevronLeft size={videoEdgeNavIconSize} strokeWidth={2.2} />
      </button>
      <button
        type="button"
        className="video-practice-overlay-button video-practice-overlay-next"
        onClick={(event) => handlePracticeOverlayAction(event, () => navigateToAdjacentSegment('next'))}
        disabled={adjacentSegmentNavigationDisabled}
        aria-label={`Next ${navigationUnitLabel} (Right arrow)`}
        title={`Next ${navigationUnitLabel} (→)`}
        tabIndex={isPracticeOverlayVisible ? 0 : -1}
      >
        <ChevronRight size={videoEdgeNavIconSize} strokeWidth={2.2} />
      </button>
      <button
        type="button"
        className="video-practice-overlay-button video-practice-overlay-up"
        onClick={(event) => handlePracticeOverlayAction(event, () => stepPlaybackSpeed(1))}
        aria-label="Faster (Up arrow)"
        title="Faster (↑)"
        tabIndex={isPracticeOverlayVisible ? 0 : -1}
      >
        <ChevronUp size={videoEdgeNavIconSize} strokeWidth={2.2} />
      </button>
      <button
        type="button"
        className="video-practice-overlay-button video-practice-overlay-down"
        onClick={(event) => handlePracticeOverlayAction(event, () => stepPlaybackSpeed(-1))}
        aria-label="Slower (Down arrow)"
        title="Slower (↓)"
        tabIndex={isPracticeOverlayVisible ? 0 : -1}
      >
        <ChevronDown size={videoEdgeNavIconSize} strokeWidth={2.2} />
      </button>
      {speedFlash && (
        <div key={speedFlash.key} className="video-speed-flash" role="status" aria-live="polite">
          {speedFlash.text}
        </div>
      )}
    </div>
  );

  const renderComboLaneModeIcon = () => {
    if (!showMergedTimeline) return <Eye size={modeControlIconSize} />;
    if (!showAllCombos) return <Layers size={modeControlIconSize} />;
    return <EyeOff size={modeControlIconSize} />;
  };

  const renderTimelineModeControls = (placement: 'studio' | 'timeline' = 'timeline') => (
    <div
      className={`timeline-mode-control-row ${placement === 'studio' ? 'timeline-mode-control-row-studio' : ''} ${isActionEditMode ? 'is-edit-mode' : 'is-combo-mode'} ${isFullscreen ? 'timeline-mode-control-row-fullscreen' : ''}`}
      role="toolbar"
      aria-label={isActionEditMode ? 'Segment edit actions' : 'Combo lane actions'}
    >
      {isActionEditMode ? (
        <>
          <button
            type="button"
            className="timeline-mode-icon-button"
            onClick={handleUndoModeAction}
            disabled={!canUndoModeAction}
            title={canUndoModeAction ? 'Undo last action' : 'Nothing to undo'}
            aria-label="Undo last action"
          >
            <Undo2 size={modeControlIconSize} />
          </button>
          <button
            type="button"
            className={`timeline-mode-icon-button ${segmentVisibilityTarget?.isDisabled ? 'is-active' : ''}`}
            onClick={handleToggleModeControlSegmentVisibility}
            disabled={!segmentVisibilityTarget || isDemo}
            title={isDemo ? 'Demo segments cannot be hidden' : segmentVisibilityControlTitle}
            aria-label={segmentVisibilityControlTitle}
          >
            {segmentVisibilityTarget?.isDisabled
              ? <Eye size={modeControlIconSize} />
              : <EyeOff size={modeControlIconSize} />}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="timeline-mode-icon-button"
            onClick={handleUndoModeAction}
            disabled={!canUndoModeAction}
            title={canUndoModeAction ? 'Undo last change' : 'Nothing to undo'}
            aria-label="Undo last change"
          >
            <Undo2 size={modeControlIconSize} />
          </button>
          <button
            type="button"
            className="timeline-mode-icon-button"
            onClick={handleEnterSegmentEditMode}
            disabled={isDemo}
            title={isDemo ? 'Demo segments cannot be edited' : 'Edit segments'}
            aria-label="Edit segments"
          >
            <Edit2 size={modeControlIconSize} />
          </button>
          <button
            type="button"
            className={`timeline-mode-icon-button ${showMergedTimeline ? 'is-active' : ''}`}
            onClick={handleCycleComboLaneVisibility}
            disabled={!hasComboSegments}
            title={comboLaneModeControlTitle}
            aria-label={comboLaneModeControlTitle}
          >
            {renderComboLaneModeIcon()}
          </button>
        </>
      )}
    </div>
  );
  const studioHeaderModeControls = renderTimelineModeControls('studio');
  const shouldRenderPracticeSurface = hasPracticeMedia || videoState.isLoading;
  const shouldRenderPlayerTimelineDeck = hasPracticeMedia && !videoState.isLoading;
  const playerTimelineDeck = shouldRenderPlayerTimelineDeck ? (
    <div
      className={`player-timeline-deck ${timelineDeckLaneClassName} player-timeline-deck-${effectiveTimelineLanePlacement} timeline-lane-placement-${effectiveTimelineLanePlacement} ${isFullscreen ? 'player-timeline-deck-fullscreen' : ''}`}
      data-testid="player-timeline-deck"
      data-lane-placement={effectiveTimelineLanePlacement}
      data-lane-stack-size={timelineDeckHasComboLane ? 'combo' : 'single'}
    >
      {isBoundaryEditorOpen ? (
        <BoundaryTimelineEditor
          duration={videoState.duration}
          currentTime={videoState.currentTime}
          segments={boundaryDraftSegments}
          activeSegment={segmentState.activeSegment}
          zoomLevel={timelineZoomLevel}
          lanePlacement={effectiveTimelineLanePlacement}
          showTimeWindow={false}
          onSeek={seekToTimeActive}
          onSegmentClick={playSegment}
          onSegmentsChange={handleBoundaryDraftChange}
          onLanePlacementChange={undefined}
        />
      ) : (
        <Timeline
          timelineRef={timelineRef}
          duration={videoState.duration}
          currentTime={videoState.currentTime}
          segments={segmentState.segments}
          activeSegment={segmentState.activeSegment}
          activeComboId={segmentState.activeComboId}
          isMarkingSegment={segmentState.isMarkingSegment}
          currentSegmentStart={segmentState.currentSegmentStart}
          showComboLane={showMergedTimeline}
          showAllCombos={showAllCombos}
          zoomLevel={timelineZoomLevel}
          lanePlacement={effectiveTimelineLanePlacement}
          videoSrc={null}
          hoverPreviewMode={youtubePractice ? 'compact' : 'none'}
          showTimeWindow={false}
          showProgressLane={false}
          showOverlapPatch={isAdvancedControlsOpen}
          segmentOverlap={segmentState.segmentOverlap}
          smoothScrub
          onTimelineClick={handleTimelineClick}
          onSeek={seekToTimeActive}
          onScrubStart={handleSmoothScrubStart}
          onScrubPreview={handleSmoothScrubPreview}
          onScrubEnd={handleSmoothScrubEnd}
          onSegmentClick={playSegment}
          onResizeComboEdge={resizeComboEdge}
          onSegmentOverlapChange={handleSegmentOverlapChange}
          onLanePlacementChange={undefined}
        />
      )}

    </div>
  ) : null;

  // Keyboard: Space = play/pause, Left/Right = previous/next segment (or combo), Up/Down = speed,
  // F = fullscreen (Esc leaves it, handled by the browser).
  // The latest handler lives in a ref so the window listener is attached only once.
  const practiceKeyHandlerRef = useRef<(event: KeyboardEvent) => void>(() => {});
  practiceKeyHandlerRef.current = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!hasPracticeMedia || videoState.isLoading || shouldShowOrientationCheck || isCropEditorOpen) return;
    if (showLibrary || showShareModal || showSegmentEditor || isPurchaseOpen) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="slider"], [role="spinbutton"]')) {
      return;
    }

    switch (event.key) {
      case ' ':
      case 'Spacebar':
        // Also stops Space from "clicking" whichever button happens to have focus.
        event.preventDefault();
        if (!event.repeat) handleTogglePlay();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        navigateToAdjacentSegment('previous');
        break;
      case 'ArrowRight':
        event.preventDefault();
        navigateToAdjacentSegment('next');
        break;
      case 'ArrowUp':
        event.preventDefault();
        stepPlaybackSpeed(1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        stepPlaybackSpeed(-1);
        break;
      case 'f':
      case 'F':
        if (event.repeat) return;
        event.preventDefault();
        toggleFullscreen();
        break;
      default:
        return;
    }
    revealPracticeOverlay();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => practiceKeyHandlerRef.current(event);
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div 
      style={containerStyle}
      ref={containerRef}
      className={`dance-video-segmenter ${practiceMode ? 'practice-mode' : ''} ${isFullscreen ? 'is-fullscreen' : 'is-windowed'} ${forceSingleControlRow ? 'is-mobile-layout' : 'is-desktop-layout'} ${isMobileFullscreenSidebarMode && isFullscreenSegmentsPanelOpen ? 'mobile-fullscreen-sidebar-open' : ''}`}
    >
      <input
        ref={checkpointsFileInputRef}
        type="file"
        accept=".json"
        onChange={(event) => {
          handleLoadCheckpoints(event);
          event.currentTarget.value = '';
        }}
        className="hidden-input"
      />
      <input
        ref={videoFileInputRef}
        type="file"
        accept={VIDEO_INPUT_ACCEPT}
        onChange={handleFileSelect}
        className="hidden-input"
      />

      {!isFullscreen && (
        <header className="player-shell-header">
          <div className="utility-auth-floating-slot">
            <AuthButton
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
              onSignInGoogle={handleSignInGoogle}
              onSignInEmail={handleSignInEmail}
              onSignUpEmail={handleSignUpEmail}
              onResetPassword={handleResetPassword}
              onSignOut={handleSignOut}
              onDeleteAccount={handleDeleteAccount}
              onShowPurchaseModal={paymentUpgradeEnabled ? () => setPurchaseOpen(true) : undefined}
            />
          </div>
          {paymentUpgradeEnabled && isAuthenticated && userProfile && (
            <PurchaseModal
              isOpen={isPurchaseOpen}
              onClose={() => setPurchaseOpen(false)}
              userProfile={userProfile}
            />
          )}
          <div className="utility-left-floating-slot">
            <button
              onClick={() => setShowLibrary(true)}
              className="utility-button utility-top-action-button utility-icon-only-button utility-button-library"
              style={{
                opacity: 1
              }}
              title={isAuthenticated ? 'Open your saved videos' : 'Open videos saved on this device'}
              aria-label={isAuthenticated ? 'Open your saved videos' : 'Open videos saved on this device'}
            >
              <Library size={utilityIconSize} />
            </button>
          </div>
          <div className="player-brand-row">
            <h1
              className="player-brand-title player-brand-title-clickable"
              onClick={handleBrandActivation}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleBrandActivation();
                }
              }}
              role="button"
              tabIndex={0}
              title="Return to upload screen"
              aria-label="Croop — return to upload screen"
            >
              <CroopBrandLogo
                key={brandAnimationKey}
                className="player-brand-logo"
              />
            </h1>
          </div>

          {isDemo && (
            <div className="demo-info-banner">
              <strong>Try It Out:</strong> Click segments to practice, create combos, try fullscreen mode, and use loop controls.
            </div>
          )}
        </header>
      )}

      {/* Sidebar for fullscreen */}
      <Sidebar
        isFullscreen={isFullscreen}
        practiceMode={practiceMode}
        segments={segmentState.segments}
        activeSegment={segmentState.activeSegment}
        activeComboId={segmentState.activeComboId}
        onSegmentClick={handleSidebarSegmentClick}
        onComboClick={handleSidebarComboClick}
        editingSegment={editingSegment}
        segmentNameInputRef={segmentNameInputRef}
        onSaveSegmentName={handleSaveSegmentName}
        onStartEditingSegment={startEditingSegment}
        onCancelEditingSegment={stopEditingSegment}
        onToggleSegmentLearned={toggleSegmentLearned}
        onToggleSegmentDisabled={handleToggleSegmentDisabled}
        onDeleteSegment={deleteSegment}
        onBatchToggleDisabledSelected={handleBatchToggleDisabledSelectedSegments}
        onBatchDeleteSelected={handleBatchDeleteSelectedSegments}
        onCreateCombo={handleCreateComboFromActiveSegment}
        onDuplicateCombo={handleDuplicateCombo}
        onDeleteCombo={deleteCombo}
        activeStudioTab={activeStudioTab}
        onActiveStudioTabChange={handleStudioTabChange}
        isLooping={segmentState.isLooping}
        videoSrc={videoState.videoSrc}
        onToggleAllLearned={handleToggleAllLearned}
        showStudioTabs={showStudioTabs}
        headerControl={studioHeaderModeControls}
        playingSegmentId={playingSourceSegmentId}
      />

      {isMobileFullscreenSidebarMode && isFullscreenSegmentsPanelOpen && (
        <button
          type="button"
          className="advanced-controls-backdrop"
          onClick={() => setIsFullscreenSegmentsPanelOpen(false)}
          aria-label="Close segments panel"
        />
      )}

      {/* Studio panel tab on the right edge. Phones: slides the panel over the video. Desktop:
          shows/hides the sidebar next to the video. */}
      {isFullscreen && (
        <button
          type="button"
          className={`fullscreen-controls-panel-toggle ${isStudioPanelShown ? 'is-panel-open' : ''} ${isMobileControlsViewport ? 'is-touch' : 'is-desktop'}`}
          onClick={toggleStudioPanel}
          aria-expanded={isStudioPanelShown}
          aria-label={isStudioPanelShown ? 'Hide segments panel' : 'Show segments panel'}
          title={isStudioPanelShown ? 'Hide segments panel' : 'Show segments panel'}
        >
          <span className="fullscreen-controls-panel-toggle-arrow" aria-hidden="true">
            {isStudioPanelShown ? <ChevronRight size={26} /> : <ChevronLeft size={26} />}
          </span>
        </button>
      )}

      {/* Main content */}
      {shouldRenderPracticeSurface && (
      <div className="player-practice-surface" data-testid="player-surface">
        <div
          className={playerStageControlStackClassName}
          data-lane-placement={effectiveTimelineLanePlacement}
          data-lane-stack-size={timelineDeckHasComboLane ? 'combo' : 'single'}
          data-testid="player-stage-control-stack"
        >
          <div
            style={videoContainerStyle}
            className="fullscreen-video-container video-stage-shell"
            data-testid="video-stage-shell"
          >
            {/* Upload video player */}
            {videoState.videoSrc && (
              <div
                ref={videoStageRef}
                className={`video-stage ${isCropEditorOpen ? 'has-crop-editor' : ''} ${videoState.isLoading ? 'video-stage-hidden' : ''} ${isMobileControlsViewport ? 'video-stage-touch' : ''}`}
              >
                <VideoCropViewport
                  aspectRatio={cropEditorAspectRatio}
                  mediaKind="uploaded"
                  fitMode={mediaFitMode}
                  isMirrored={videoState.isMirrored}
                  viewCrop={renderedViewCrop}
                >
                  <video
                    ref={videoRef}
                    src={videoState.videoSrc}
                    playsInline
                    className={`segmenter-video ${isFullscreen ? 'segmenter-video-fullscreen' : 'segmenter-video-windowed'}`}
                    onLoadedData={() => {
                      handleVideoLoaded(true);
                      // Library projects reopen on the segment/combo that was playing when saved.
                      const pendingId = pendingDefaultComboSeekRef.current;
                      pendingDefaultComboSeekRef.current = null;
                      const pendingSegment = pendingId
                        ? playbackSegments.find(segment => segment.id === pendingId)
                        : undefined;
                      if (pendingSegment) seekToTime(getLoopStartTime(pendingSegment));
                    }}
                    onError={handleVideoLoadError}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleMediaEnded}
                    onPlay={() => setVideoState(prev => ({ ...prev, isPlaying: true }))}
                    onPause={() => setVideoState(prev => ({ ...prev, isPlaying: false }))}
                  />
                </VideoCropViewport>
                {renderPracticeOverlay()}
                {shouldShowOrientationCheck && (
                  <OrientationCheckOverlay
                    isMirrored={videoState.isMirrored}
                    onSelectOrientation={handleSelectOrientation}
                    onConfirm={handleConfirmOrientation}
                  />
                )}
                {isCropEditorOpen && (
                  <VideoCropEditorOverlay
                    stageRef={videoStageRef}
                    value={draftViewCrop}
                    aspectRatio={cropEditorAspectRatio}
                    isMirrored={videoState.isMirrored}
                    hasAppliedCrop={hasViewCrop}
                    onChange={setDraftViewCrop}
                    onApply={handleApplyViewCrop}
                    onReset={handleResetViewCrop}
                    onCancel={handleCancelCropEditor}
                  />
                )}
              </div>
            )}
            {/* YouTube practice player */}
            {youtubePractice && (
              <div
                ref={videoStageRef}
                className={`video-stage youtube-stage ${isCropEditorOpen ? 'has-crop-editor' : ''} ${isMobileControlsViewport ? 'video-stage-touch' : ''}`}
              >
                <YouTubePracticePlayer
                  key={youtubePractice.runtimeKey}
                  videoId={youtubePractice.videoId}
                  startTime={youtubePractice.startTime}
                  fitMode={mediaFitMode}
                  isMirrored={videoState.isMirrored}
                  viewCrop={renderedViewCrop}
                  allowAutoPlay
                  onAdapterReady={(adapter) => {
                    youtubeAdapterRef.current = adapter;
                    // Apply the current controls so the YouTube player matches UI state.
                    const rate = getSupportedYouTubePlaybackRate(videoState.playbackRate);
                    if (rate !== videoState.playbackRate) {
                      setVideoState(prev => ({ ...prev, playbackRate: rate }));
                    }
                    if (rate !== 1) adapter.setSpeed(rate);
                    adapter.mute(videoState.isMuted);
                    // Curated default combo: jump to its loop start (the
                    // player autoplays; loop detection takes it from there).
                    const defaultComboId = pendingDefaultComboSeekRef.current;
                    pendingDefaultComboSeekRef.current = null;
                    const defaultCombo = defaultComboId
                      ? playbackSegments.find(segment => segment.id === defaultComboId)
                      : undefined;
                    if (defaultCombo) adapter.seek(getLoopStartTime(defaultCombo));
                    // Play straight away. If YouTube refuses to autoplay with sound, retry muted so
                    // the video still starts (the mute button shows it; one tap turns sound on).
                    youtubeHasPlayedRef.current = false;
                    adapter.play();
                    let autoplayChecks = 0;
                    const checkAutoplay = () => {
                      if (youtubeAdapterRef.current !== adapter || youtubeHasPlayedRef.current) return;
                      // Still loading (slow connection): wait a bit longer before deciding it was blocked.
                      if (adapter.isBuffering() && autoplayChecks < 4) {
                        autoplayChecks += 1;
                        window.setTimeout(checkAutoplay, YOUTUBE_AUTOPLAY_FALLBACK_MS);
                        return;
                      }
                      adapter.mute(true);
                      adapter.play();
                      setVideoState(prev => ({ ...prev, isMuted: true }));
                    };
                    window.setTimeout(checkAutoplay, YOUTUBE_AUTOPLAY_FALLBACK_MS);
                  }}
                  onTimeUpdate={handleYouTubeTimeUpdate}
                  onPlayStateChange={handleYouTubePlayStateChange}
                  onEnded={handleMediaEnded}
                />
                {renderPracticeOverlay()}
                {shouldShowOrientationCheck && (
                  <OrientationCheckOverlay
                    isMirrored={videoState.isMirrored}
                    onSelectOrientation={handleSelectOrientation}
                    onConfirm={handleConfirmOrientation}
                  />
                )}
                {isCropEditorOpen && (
                  <VideoCropEditorOverlay
                    stageRef={videoStageRef}
                    value={draftViewCrop}
                    aspectRatio={cropEditorAspectRatio}
                    isMirrored={videoState.isMirrored}
                    hasAppliedCrop={hasViewCrop}
                    onChange={setDraftViewCrop}
                    onApply={handleApplyViewCrop}
                    onReset={handleResetViewCrop}
                    onCancel={handleCancelCropEditor}
                  />
                )}
              </div>
            )}
          </div>

          {/* Controls */}
          <div
            style={controlsSurfaceStyle}
            className={playerControlsShellClassName}
            data-testid="player-controls-shell"
            data-lane-placement={effectiveTimelineLanePlacement}
            onMouseMove={isFullscreen ? revealFullscreenControls : undefined}
            onTouchStart={isFullscreen ? revealFullscreenControls : undefined}
          >
            {playerTimelineDeck}

  {/* Video controls */}
  {videoState.isLoading && (
    <LoadingIndicator loadingSource={videoState.loadingSource} />
  )}

  {(videoState.videoSrc || youtubePractice) && !videoState.isLoading && (
    <div className="controls-stack">
      <div className={`controls-panel ${isFullscreen ? 'controls-panel-fullscreen' : ''}`}>
        <div
          className={`controls-row controls-row-primary controls-row-primary-even ${isCompactPrimaryControls ? 'controls-row-primary-compact' : ''} ${isUltraCompactPrimaryControls ? 'controls-row-primary-ultra' : ''} ${isPhonePrimaryControls ? 'controls-row-primary-phone' : ''} ${isPocketPrimaryControls ? 'controls-row-primary-pocket' : ''}`}
        >
          {/* Five equal cells with the main action in the exact centre. Practice:
              mute · combo left | loop | combo right · settings. Editing (no looping):
              mute · back 1s | Segment | Save · settings. */}
          <div className="primary-controls-left">
            <button
              className="transport-control-button transport-control-button-volume"
              onClick={handleToggleMute}
              style={controlIconStyle(videoState.isMuted ? MUTE_ACTIVE_COLOR : restIconColor)}
              title={muteControlTitle}
              aria-pressed={videoState.isMuted}
              aria-label={videoState.isMuted ? 'Muted' : 'Unmuted'}
            >
              {videoState.isMuted ? <VolumeX size={controlIconSize} /> : <Volume2 size={controlIconSize} />}
            </button>
            {!isDemo && !isActionEditMode && (
              <button
                type="button"
                className="transport-control-button combo-extend-button combo-extend-button-left"
                onClick={() => handleExtendCombo('left')}
                disabled={!canExtendComboLeft}
                style={controlIconStyle(canExtendComboLeft ? COMBO_SEGMENT_COLOR : disabledIconColor)}
                title={canExtendComboLeft ? 'Combo to the left: add the segment before' : 'No segment to the left'}
                aria-label="Combo to the left"
              >
                <ComboExtendGlyph direction="left" size={controlIconSize + 4} />
              </button>
            )}
            {!isDemo && isActionEditMode && (
              <button
                type="button"
                className="transport-control-button step-back-button"
                onClick={() => seekBySecondsActive(-1)}
                style={controlIconStyle(restIconColor)}
                title="Back 1 second"
                aria-label="Back 1 second"
              >
                <ChevronsLeft size={controlIconSize + 2} />
                <span className="step-back-label" aria-hidden="true">1s</span>
              </button>
            )}
          </div>

          <div className="primary-controls-center primary-controls-center-loop">
            {/* Editing has no looping: Segment takes the centre instead. */}
            {!isDemo && isActionEditMode ? (
            <button
              className="primary-center-action primary-center-action-segment segment-hero-button"
              onClick={endSegment}
              disabled={!canEndSegment}
              aria-label="Segment"
              style={{
                '--primary-center-action-bg': canEndSegment
                  ? PASTEL_SEGMENT_COLOR
                  : segmentState.isLooping
                  ? '#8f88a7'
                  : BUTTON_COLORS.DISABLED,
                '--primary-center-action-hover-bg': canEndSegment
                  ? PASTEL_SEGMENT_HOVER_COLOR
                  : segmentState.isLooping
                  ? '#8f88a7'
                  : BUTTON_COLORS.DISABLED,
                minHeight: `${controlButtonSize}px`,
                height: `${controlButtonSize}px`,
                padding: controlButtonSize <= 30 ? '0 14px' : '0 20px',
                color: 'white',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: canEndSegment ? 'pointer' : 'not-allowed',
                opacity: !isBoundaryEditorOpen && segmentState.isLooping ? 0.82 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem',
                fontSize: controlButtonSize <= 30 ? '0.78rem' : '0.85rem',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              } as React.CSSProperties & Record<`--${string}`, string>}
              title={segmentActionTitle}
            >
              {/* The button fills its whole cell (all of it is tappable); the purple pill is drawn inside. */}
              <span className="primary-center-action-pill">
                <Crop size={controlIconSize} />
                <span className="primary-center-action-label">Segment</span>
              </span>
            </button>
            ) : (
              <button
                className={`transport-control-button transport-control-button-loop loop-hero-button ${segmentState.isLooping ? 'is-looping' : ''}`}
                onClick={handleToggleLoop}
                style={{
                  ...controlIconStyle(segmentState.isLooping ? 'var(--loop-hero-accent)' : restIconColor),
                  '--loop-hero-accent': segmentState.isLooping && activeLoopComboRange ? COMBO_SEGMENT_COLOR : LOOP_HERO_COLOR
                } as React.CSSProperties & Record<`--${string}`, string>}
                title={segmentState.isLooping ? 'Loop on' : 'Loop off'}
                aria-pressed={segmentState.isLooping}
                aria-label={segmentState.isLooping ? 'Loop on' : 'Loop off'}
              >
                <LoopGlyph
                  color="currentColor"
                  size={controlIconSize + 8}
                />
              </button>
            )}
          </div>

          <div className="primary-controls-right">
            {/* Editing: Save sits opposite "back 1s", mirroring combo left / combo right. */}
            {isDemo && <span className="primary-controls-placeholder" aria-hidden="true" />}
            {!isDemo && isActionEditMode && (
              <button
                type="button"
                className="primary-center-action primary-center-action-save"
                onClick={handleSaveAndExitSegmentEditMode}
                disabled={!canSaveLocal}
                title={savePracticeControlTitle}
                aria-label="Save segments and practice"
                style={{
                  '--primary-center-action-bg': canSaveLocal ? SAVE_ACTION_COLOR : BUTTON_COLORS.DISABLED,
                  '--primary-center-action-hover-bg': canSaveLocal ? SAVE_ACTION_HOVER_COLOR : BUTTON_COLORS.DISABLED,
                  minHeight: `${controlButtonSize}px`,
                  height: `${controlButtonSize}px`,
                  color: 'white',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: canSaveLocal ? 'pointer' : 'not-allowed',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: controlButtonSize <= 30 ? '0.78rem' : '0.85rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                } as React.CSSProperties & Record<`--${string}`, string>}
              >
                <span className="primary-center-action-pill">
                  <Save size={controlIconSize} />
                  <span className="primary-center-action-label">Save</span>
                </span>
              </button>
            )}
            {!isDemo && !isActionEditMode && (
              <button
                type="button"
                className="transport-control-button combo-extend-button combo-extend-button-right"
                onClick={() => handleExtendCombo('right')}
                disabled={!canExtendComboRight}
                style={controlIconStyle(canExtendComboRight ? COMBO_SEGMENT_COLOR : disabledIconColor)}
                title={canExtendComboRight ? 'Combo to the right: add the segment after' : 'No segment to the right'}
                aria-label="Combo to the right"
              >
                <ComboExtendGlyph direction="right" size={controlIconSize + 4} />
              </button>
            )}
            <button
              onClick={toggleAdvancedControls}
              style={advancedToggleStyle}
              title={advancedControlsTitle}
              aria-label={advancedControlsTitle}
            >
              <SlidersHorizontal size={controlIconSize} />
            </button>
          </div>
        </div>

        {showAdvancedControlsRow && (
          <div className={`controls-row controls-row-secondary controls-row-advanced ${forceSingleControlRow && isAdvancedControlsOpen ? 'controls-row-scroll' : ''}`}>
            {isAdvancedControlsOpen && (
              <>
                <div className="advanced-controls-left">
                  <button
                    onClick={toggleMirror}
                    className="orientation-toggle-button"
                    style={flatLabelStyle(videoState.isMirrored ? mirrorAccentColor : restIconColor)}
                    title={videoState.isMirrored ? 'Currently Mirrored. Click for Original.' : 'Currently Original. Click for Mirrored.'}
                    aria-label={videoState.isMirrored ? 'Current orientation Mirrored. Switch to Original.' : 'Current orientation Original. Switch to Mirrored.'}
                  >
                    <FlipHorizontal size={controlIconSize} />
                    <span>{videoState.isMirrored ? 'Mirrored' : 'Original'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleCropEditor}
                    className={`crop-view-control-button ${hasViewCrop ? 'is-active' : ''} ${isCropEditorOpen ? 'is-editor-open' : ''}`}
                    style={cropViewButtonStyle}
                    title="Crop view box"
                    aria-label="Crop view box"
                    aria-pressed={isCropEditorOpen}
                    aria-expanded={isCropEditorOpen}
                    aria-controls={isCropEditorOpen ? 'crop-view-editor-shell' : undefined}
                    data-testid="advanced-crop-view-button"
                    data-crop-active={hasViewCrop ? 'true' : 'false'}
                  >
                    <SquareScissors size={controlIconSize} strokeWidth={2.05} />
                  </button>
                </div>
                <div className="advanced-controls-center">
                  <div className="timeline-zoom-group" aria-label="Timeline zoom controls">
                    <button
                      type="button"
                      className="settings-zoom-out"
                      onClick={handleTimelineZoomOut}
                      disabled={!canZoomTimelineOut}
                      style={secondaryIconStyle(!canZoomTimelineOut)}
                      title={`Zoom timeline out (${timelineZoomLevel}x)`}
                      aria-label="Zoom timeline out"
                    >
                      <ZoomOut size={controlIconSize} />
                    </button>
                    <button
                      type="button"
                      className="settings-zoom-in"
                      onClick={handleTimelineZoomIn}
                      disabled={!canZoomTimelineIn}
                      style={secondaryIconStyle(!canZoomTimelineIn)}
                      title={`Zoom timeline in (${timelineZoomLevel}x)`}
                      aria-label="Zoom timeline in"
                    >
                      <ZoomIn size={controlIconSize} />
                    </button>
                  </div>
                </div>
                <div className="advanced-controls-right">
                  <SpeedTextInput
                    playbackRate={videoState.playbackRate}
                    onCommit={applyPlaybackRate}
                    allowedRates={youtubePractice ? YOUTUBE_SPEED_PRESETS : undefined}
                    controlHeight={controlButtonSize}
                    isFullscreen={isFullscreen}
                  />
                  <div
                    className="stepper-slot advanced-overlap-slot"
                    style={{
                      // Sized to its content so "Overlap" is never clipped.
                      flex: '0 0 auto',
                      minWidth: 0,
                      maxWidth: 'none'
                    }}
                  >
                    <OverlapSlider
                      segmentOverlap={segmentState.segmentOverlap}
                      setSegmentOverlap={handleSegmentOverlapChange}
                      maxOverlapSeconds={maxSegmentOverlapSeconds}
                      isFullscreen={isFullscreen}
                      controlHeight={controlButtonSize}
                    />
                  </div>
                  <span className="settings-row-divider" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    className="settings-fullscreen-button"
                    style={secondaryIconStyle(false)}
                    title={isFullscreen
                      ? 'Exit full screen. Shortcut: double-tap the video, or press F or Esc.'
                      : 'Full screen. Shortcut: double-tap the video, or press F.'}
                    aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
                  >
                    {isFullscreen ? <Minimize size={controlIconSize} /> : <Maximize size={controlIconSize} />}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Action rows - only show when NOT in fullscreen and NOT in demo */}
      {!isFullscreen && !isDemo && (
        <div
          className="secondary-actions-shell"
        >
          {/* Share row */}
          {sharingEnabled && library.isSavedToLibrary && (
            <div className="share-row-shell">
              <button
                onClick={() => setShowShareModal(true)}
                disabled={!library.canShare}
                style={{
                  ...successButtonStyle,
                  backgroundColor: !library.canShare
                    ? BUTTON_COLORS.DISABLED
                    : currentShareData
                    ? BUTTON_COLORS.SUCCESS
                    : BUTTON_COLORS.PURPLE,
                  cursor: !library.canShare ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  justifyContent: 'center',
                  position: 'relative',
                }}
                title={library.getShareButtonTooltip()}
              >
                <Share
                  size={UI_DIMENSIONS.ICON_SIZE_SMALL}
                  className="share-icon"
                />
                {currentShareData ? getShareButtonText() : 'Share Video'}
                {/* Active indicator dot */}
                {currentShareData && (
                  <span className="share-active-dot" />
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )}
          </div>
        </div>
      </div>
      )}

      {/* Segments list - only show when not in fullscreen */}
      {!isFullscreen && (
        <div className="segments-list-shell">
          <SegmentsList
            segments={segmentState.segments}
            activeSegment={segmentState.activeSegment}
            activeComboId={segmentState.activeComboId}
            isLooping={segmentState.isLooping}
            editingSegment={editingSegment}
            segmentNameInputRef={segmentNameInputRef}
            onSaveSegmentName={handleSaveSegmentName}
            onStartEditingSegment={startEditingSegment}
            onCancelEditingSegment={stopEditingSegment}
            onPlaySegment={playSegment}
            onSelectCombo={handleStudioComboClick}
            onToggleSegmentLearned={toggleSegmentLearned}
            onToggleSegmentDisabled={handleToggleSegmentDisabled}
            onDeleteSegment={deleteSegment}
            onBatchToggleDisabledSelected={handleBatchToggleDisabledSelectedSegments}
            onBatchDeleteSelected={handleBatchDeleteSelectedSegments}
            onCreateCombo={handleCreateComboFromActiveSegment}
            onDuplicateCombo={handleDuplicateCombo}
            onDeleteCombo={deleteCombo}
            activeTab={activeStudioTab}
            onActiveTabChange={handleStudioTabChange}
            videoSrc={videoState.videoSrc}
            onToggleAllLearned={handleToggleAllLearned}
            showTabs={showStudioTabs}
            playingSegmentId={playingSourceSegmentId}
            headerControl={
              <>
                {studioHeaderModeControls}
                {devToolsEnabled && youtubePractice && (
                  <button
                    type="button"
                    className="studio-list-icon-button"
                    onClick={() => handleExportCuratedPreset(segmentState.segments)}
                    aria-label="Copy as curated preset"
                    title="Copy current segments/combos as a curated preset (dev only)"
                  >
                    <ClipboardCopy size={15} />
                  </button>
                )}
              </>
            }
          />
        </div>
  )}

      {/* Empty state — shown only when no video (upload or YouTube) is active */}
      {!videoState.videoSrc && !videoState.isLoading && !youtubePractice && (
        <div className="empty-state-shell">
          <LauncherVariantA
            onChooseFile={triggerVideoFilePicker}
            onPracticeYouTube={handlePracticeYouTube}
          />
        </div>
      )}

      {/* NEW MODERN SHARE MODAL */}
      {sharingEnabled && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareUrl={currentShareData ? `${window.location.origin}/?share=${currentShareData.shareId}` : null}
          shareData={currentShareData}
          onCreateNew={handleCreateShare}
          onRevoke={handleRevokeShare}
          isCreating={isCreatingShare}
          videoTitle={library.videoTitle || videoState.videoFileName}
        />
      )}

      {/* Segment Editor Modal */}
      {showSegmentEditor && (
      <SegmentEditor
        segments={segmentState.segments}
        currentTime={videoState.currentTime}
        onUpdateSegments={handleUpdateSegmentsFromEditor}
        onClose={() => setShowSegmentEditor(false)}
      />
    )}

      {/* Library Viewer Modal */}
      {showLibrary && (
        <LibraryViewer
          isOpen={showLibrary}
          onClose={() => setShowLibrary(false)}
          onLoadVideo={handleLoadFromLibrary}
          userProfile={userProfile}
          refreshProfile={refreshProfile}
        />
      )}

      {/* Purchase Modal */}
      {paymentUpgradeEnabled && isAuthenticated && userProfile && (
        <PurchaseModal
          isOpen={isPurchaseOpen}
          onClose={() => setPurchaseOpen(false)}
          userProfile={userProfile}
        />
      )}

      {/* Footer - only show when not in fullscreen */}
      {!isFullscreen && (
        <footer className="player-footer">
          <div className="player-footer-note">
            Built by <strong>{APP_INFO.AUTHOR}</strong> for dance enthusiasts.
          </div>
          <div className="player-footer-links">
            <a href={`mailto:${APP_INFO.EMAIL}`} className="player-footer-link">{APP_INFO.EMAIL}</a>
            {' • '}
            <a href={APP_INFO.INSTAGRAM} target="_blank" rel="noopener noreferrer" className="player-footer-link">Instagram</a>
            {' • '}
            <a href={APP_INFO.GITHUB} target="_blank" rel="noopener noreferrer" className="player-footer-link">GitHub</a>
          </div>
          {isDemo && (
            <div className="player-footer-demo-credits">
              Demo Credits: Choreography by {DEMO_CREDITS.CHOREOGRAPHER} • Performed by {DEMO_CREDITS.PERFORMER}
            </div>
          )}
        </footer>
      )}
    </div>
  );
};

export default DanceVideoSegmenter;
